import { isQuotationUsable, lineGross, printedLineNote, resolveGstMode, resolvePlaceOfSupply, placeOfSupplyProblem } from "@jira-clone/shared-types";
import { PlaceOfSupplyField } from "@/components/sales/PlaceOfSupplyField";
import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { LockedHint, MasterDataMissingBanner } from "@/components/sales/MasterDataNotice";
import { BaseRateWarningBanner } from "@/components/sales/BaseRateNotice";
import { findBaseRateViolations, baseRateWarningText, type BaseRateViolation } from "@/lib/baseRate";
import { serviceLineFields, resolveTaxMasterId } from "@/lib/serviceLines";
import { TaxSlabSelect } from "@/components/sales/TaxSlabSelect";
import {
  LOCKED_INPUT_CLASS,
  describeMasterDataIssues,
  hasMasterDataIssues,
  missingBankDetails,
  missingCompanyDetails,
} from "@/lib/masterDataLock";
import { Plus, X, Loader2, FileText, ChevronDown, CheckCircle2, Trash2, CreditCard, Lock, ShieldCheck, ExternalLink, Building2, User, Receipt, Landmark, Calendar, Layers, FileCheck, HelpCircle } from "lucide-react";
import { Badge } from "@/shared/components/ui/Badge";
import { api } from "@/shared/api/axios";
import { getApiErrorMessage } from "@/shared/utils/apiError";
import { useInvoiceRequestActions } from "@/hooks/sales/useInvoiceRequests";
import type { InvoiceRequest } from "@/hooks/sales/useInvoiceRequests";
import { generateFullProformaInvoiceHtml, lockHtmlTemplate } from "@/utils/sales/quotationTemplateTokens";
import { parseStagePercents } from "@/utils/sales/paymentTermStages";
import { smoothScrollToTop } from "@/shared/utils/utils";
import { useUser } from "@/shared/hooks/useAuth";
import { useCompanyProfile } from "@/features/recruitment/api";
import { useDocumentMasterData, type ProductServiceData, type TaxMasterData } from "@/shared/hooks/useDocumentMasterData";
import { useQuotationDisplaySettings } from "@/shared/hooks/useQuotationDisplaySettings";
import { useQuotationDefaults } from "@/shared/hooks/useQuotationDefaults";
import { UNIT_OPTIONS } from "./QuotationCreateForm";

interface LineItem {
  key: string;
  name: string;
  description: string;
  notes?: string;
  hsnSac: string;
  quantity: number;
  unit?: string;
  rate: number;
  discountPercent: number;
  taxRate: number;
  /** Required on a proforma: the Taxes / GST slab this line is billed at. */
  taxMasterId?: string;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  catalogItemId?: string | null;
}

interface Milestone {
  key: string;
  label: string;
  percent: number;
  dueDate: string;
  triggerType?: string;
  description?: string;
  daysFromApproval?: number;
}

// Must mirror the triggerType enum in the GoodDayInvoiceRequest Milestone schema.
const VALID_TRIGGER_TYPES = ['advance', 'date_based', 'delivery', 'project_phase', 'customer_approval', 'work_completion', 'final_delivery', 'manual'];
const safeTriggerType = (t: any, idx: number, total: number) =>
  VALID_TRIGGER_TYPES.includes(t) ? t : (idx === 0 ? 'advance' : (idx === total - 1 ? 'final_delivery' : 'project_phase'));

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  asPage?: boolean;
  initialQuotationId?: string;
  initialQuotationNumber?: string;
  initialScheduleId?: string;
  /** When provided, the form opens in EDIT mode for the given draft proforma. */
  editingDraft?: InvoiceRequest | null;
  // Resubmission / Revision mode props
  isResubmission?: boolean;
  isRevision?: boolean;
  rejectionReason?: string;
  proformaNumber?: string;
  currentRevisionLabel?: string;
  nextRevisionLabel?: string;
  documentId?: string;
  /** Milestone index (0-based) and phase label for per-milestone proforma creation */
  initialMilestoneIndex?: number;
  initialMilestonePhaseLabel?: string;
  // Optional prefill data from Deal / Lead / Client
  fromDeal?: any;
  fromLead?: any;
  initialClientName?: string;
  initialClientEmail?: string;
  initialClientPhone?: string;
  initialServices?: string[];
  initialAmount?: number;
  initialSubject?: string;
  initialNotes?: string;
}

const uid = () => Math.random().toString(36).slice(2, 9);
const PAYMENT_TERM_CUSTOM = '__custom__';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

const emptyLine = (): LineItem => ({
  key: uid(),
  name: "",
  description: "",
  notes: "",
  hsnSac: "998314",
  quantity: 1,
  unit: "Nos",
  rate: 0,
  discountPercent: 0,
  taxRate: 18,
  taxMasterId: '',
});

const emptyMilestone = (): Milestone => ({
  key: uid(),
  label: "",
  percent: 0,
  dueDate: "",
});

const DEFAULT_MILESTONES = [
  { label: "Advance (on order confirmation)", percent: 30, dueDate: "" },
  { label: "Midpoint (on 50% completion)", percent: 40, dueDate: "" },
  { label: "Final (on delivery)", percent: 30, dueDate: "" },
];

const lineNet = (l: LineItem) =>
  Math.max(0, lineGross(l.quantity, l.rate, l.unit) * (1 - (l.discountPercent || 0) / 100));

const lineTax = (l: LineItem, gstOn?: boolean) => gstOn ? lineNet(l) * ((l.taxRate || 0) / 100) : 0;

// Mirror a quotation number onto a proforma: swap the QT prefix segment wherever it sits —
// master-data format `DT-QT-2026-27/0024` -> `DT-PI-2026-27/0024`, legacy `QT/2026-27/0024`
// -> `PI/2026-27/0024`. PI/TI numbers pass through unchanged. The server owns the final
// number via the master-data `proforma` numbering config; this only keeps the form in step.
const swapQuotationPrefix = (numStr?: string | null): string =>
  numStr ? String(numStr).replace(/(^|[-/])QTN?(?=[-/]|$)/gi, '$1PI') : '';

const cls =
  "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-colors";

const labelCls =
  "text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5 block";

const cardCls =
  "bg-white dark:bg-slate-900 rounded-xl border border-slate-300 dark:border-slate-600 shadow-xs p-4 sm:p-5";

// Grey canvas so the white section cards read as separate blocks.
const PAGE_BG = "bg-slate-100 dark:bg-slate-950/60";

function mapIRLine(il: any): LineItem {
  const name = il.name || il.description || il.item || "Line Item";
  // Only the rep's note is carried over; the master-data description of the service is not printed.
  const description = name;
  const notes = il.notes || printedLineNote(il);
  const hsnSac = il.hsnSac || il.hsn || "998314";
  const quantity = Number(il.quantity ?? il.qty) || 1;
  const unit = il.unit || "Nos";
  const rate = Number(il.rate ?? il.unitPrice ?? il.price) || 0;
  // A fixed-amount discount is stored as `discount` with `discountPercent`/`disc` at 0 —
  // convert it to the equivalent percent so the proforma line nets the same as the quotation.
  let discountPercent = Number(il.discountPercent ?? il.disc) || 0;
  if (!discountPercent && Number(il.discount ?? 0) > 0) {
    if (String(il.discountType || '') === 'percent') {
      discountPercent = Number(il.discount) || 0;
    } else {
      const gross = lineGross(quantity, rate, unit);
      if (gross > 0) discountPercent = Math.round((Number(il.discount) / gross) * 10000) / 100;
    }
  }
  const taxRate = Number(il.taxRate ?? il.gstRate ?? il.gst) || 18;
  const cgstRate = il.cgstRate != null ? Number(il.cgstRate) : (il.taxRate ? Math.round(Number(il.taxRate) / 2 * 100) / 100 : undefined);
  const sgstRate = il.sgstRate != null ? Number(il.sgstRate) : (il.taxRate ? Math.round(Number(il.taxRate) / 2 * 100) / 100 : undefined);
  const igstRate = il.igstRate != null ? Number(il.igstRate) : (Number(il.taxRate || il.gstRate || il.gst) > 0 ? Number(il.taxRate || il.gstRate || il.gst) : undefined);
  return {
    key: uid(),
    name,
    description,
    notes,
    hsnSac,
    quantity,
    unit,
    rate,
    discountPercent,
    taxRate,
    taxMasterId: resolveTaxMasterId(il.taxMasterId),
    cgstRate,
    sgstRate,
    igstRate,
    catalogItemId: il.catalogItemId || null,
  };
}

export function ProformaInvoiceGenerator({
  onClose,
  onSuccess,
  initialQuotationId,
  initialQuotationNumber,
  initialScheduleId,
  editingDraft,
  isResubmission = false,
  isRevision = false,
  rejectionReason,
  proformaNumber,
  currentRevisionLabel,
  nextRevisionLabel,
  documentId,
  initialMilestoneIndex,
  initialMilestonePhaseLabel,
  fromDeal,
  fromLead,
  initialClientName,
  initialClientEmail,
  initialClientPhone,
  initialServices,
  initialAmount,
  initialSubject,
  initialNotes,
  asPage,
}: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const actions = useInvoiceRequestActions();
  const currentUser = useUser();
  const companyProfile = useCompanyProfile().data;
  const { data: masterData, isLoading: masterLoading } = useDocumentMasterData();
  const termConditions = masterData?.termConditions ?? [];
  const assumptionsExclusions = masterData?.assumptionsExclusions ?? [];
  const declarations = masterData?.declarations ?? [];
  const bankDetails = masterData?.bankDetails ?? [];
  const productServices: ProductServiceData[] = masterData?.productServices ?? [];
  const taxMasters: TaxMasterData[] = masterData?.taxMasters ?? [];
  const hsnSacCodes = masterData?.hsnSacCodes ?? [];
  const displaySettings = masterData?.displaySettings ?? [];
  const quotationDefaults = masterData?.quotationDefaults;
  const assumptionsSectionEnabled = quotationDefaults?.assumptionsExclusionsEnabled === true;

  const masterServices = useMemo(() => {
    return productServices.map(s => ({
      ...s,
      defaultUnit: s.unit || s.defaultUnit || 'project',
    }));
  }, [productServices]);

  const masterServicesByCategory = useMemo(() => {
    const grouped: Record<string, ProductServiceData[]> = { TECHNOLOGY: [], MARKETING_GROWTH: [], CREATIVE: [] };
    productServices.forEach((s: any) => {
      if (grouped[s?.category]) grouped[s.category].push(s);
    });
    return grouped;
  }, [productServices]);

  const allMasterServices = useMemo(() => productServices, [productServices]);
  const userRole = (currentUser?.role || '').toLowerCase();
  const userDept = (currentUser?.department || '').toLowerCase();
  const isSuperAdmin = userRole === 'superadmin' || userRole === 'owner' || userRole === 'ceo' || userRole === 'admin' || userDept === 'superadmin' || userDept === 'management';
  // Same people the server lets approve a proforma: their own proforma is approved as they submit it.
  const approvesOwnProforma = userRole === 'superadmin' || userRole === 'owner' || userRole === 'admin' || userDept === 'superadmin';
  const isEditMode = Boolean(editingDraft?.id || (editingDraft as any)?._id);
  const editId = editingDraft?.id || (editingDraft as any)?._id || '';

  // Determine mode: resubmission takes precedence
  const isResubmitMode = isResubmission && documentId;

  // Determine if this proforma is confirmed & locked
  const isConfirmed = Boolean(
    (editingDraft as any)?.isConfirmed ||
    (editingDraft as any)?.isLocked ||
    (editingDraft as any)?.status === 'confirmed'
  );

  const [quotations, setQuotations] = useState<InvoiceRequest[]>([]);
  const [quotsLoading, setQuotsLoading] = useState(true);
  const [linkedDeal, setLinkedDeal] = useState<any>(fromDeal || null);
  const [selectedQuotId, setSelectedQuotId] = useState("");
  const [selectedQuotDoc, setSelectedQuotDoc] = useState<any>(null);
  const [schedDoc, setSchedDoc] = useState<any>(null);
  // Track whether the selected quotation uses milestone-based or full payment
  const [quotPaymentType, setQuotPaymentType] = useState<'full_payment' | 'milestone_based' | ''>('');

  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientGstin, setClientGstin] = useState("");
  const [clientState, setClientState] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactDesignation, setContactDesignation] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [subject, setSubject] = useState("");
  const [introText, setIntroText] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0]);
  const [validUntil, setValidUntil] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [customPaymentTerm, setCustomPaymentTerm] = useState(false);
  // Payment terms come from master data, the same list the quotation uses.
  const activePaymentTerms = useMemo(() => (masterData?.paymentTerms ?? []).filter(t => t.isActive !== false), [masterData?.paymentTerms]);
  const selectedPaymentTerm = useMemo(() => activePaymentTerms.find(t => t.name === paymentTerms), [activePaymentTerms, paymentTerms]);
  // A proforma with no term of its own (and none from its quotation) starts on the master-data default, once.
  const paymentTermDefaulted = useRef(false);
  useEffect(() => {
    if (paymentTermDefaulted.current || paymentTerms || activePaymentTerms.length === 0) return;
    paymentTermDefaulted.current = true;
    const def = activePaymentTerms.find(t => t.isDefault);
    if (def) setPaymentTerms(def.name);
  }, [activePaymentTerms, paymentTerms]);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [termsList, setTermsList] = useState<string[]>([]);
  const [assumptions, setAssumptions] = useState("");
  const [assumptionsList, setAssumptionsList] = useState<string[]>([]);
  const [declaration, setDeclaration] = useState("");
  const [declarationId, setDeclarationId] = useState("");
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  // Business / Sender details
  const [businessName, setBusinessName] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [businessTagline, setBusinessTagline] = useState("");
  const [businessGstin, setBusinessGstin] = useState("");
  const [businessPan, setBusinessPan] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [businessWeb, setBusinessWeb] = useState("");
  // GST config
  const [gstEnabled, setGstEnabled] = useState(true);
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [reverseCharge, setReverseCharge] = useState(false);
  // Bank details
  const [bankDetailId, setBankDetailId] = useState("");
  const [bankAccName, setBankAccName] = useState("");
  const [bankAccNo, setBankAccNo] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankBranch, setBankBranch] = useState("");
  const [bankIfsc, setBankIfsc] = useState("");
  const [bankUpi, setBankUpi] = useState("");
  // Document metadata
  const [preparedBy, setPreparedBy] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [headerDiscount, setHeaderDiscount] = useState(0);
  const [headerDiscountType, setHeaderDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [additionalChargesLabel, setAdditionalChargesLabel] = useState("");
  const [clientId, setClientId] = useState("");
  // Display options
  const [showHsnColumn, setShowHsnColumn] = useState(true);
  const [showTaxSummary, setShowTaxSummary] = useState(true);
  const [showSkuColumn, setShowSkuColumn] = useState(false);
  const [showSerialNumbers, setShowSerialNumbers] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [showDescFullWidth, setShowDescFullWidth] = useState(false);
  const [hideSubtotalForGroups, setHideSubtotalForGroups] = useState(false);
  const [hidePlaceOfSupply, setHidePlaceOfSupply] = useState(false);
  const [displayUnit, setDisplayUnit] = useState("short");
  // Shipping details
  const [shippingEnabled, setShippingEnabled] = useState(false);
  const [shippingName, setShippingName] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingPincode, setShippingPincode] = useState("");
  const ROMAN_MAP: Record<string, number> = {
    'I': 0, 'II': 1, 'III': 2, 'IV': 3, 'V': 4,
    'VI': 5, 'VII': 6, 'VIII': 7, 'IX': 8, 'X': 9,
    '1': 0, '2': 1, '3': 2, '4': 3, '5': 4,
    '6': 5, '7': 6, '8': 7, '9': 8, '10': 9,
  };

  const resolveMilestoneIndexFromDoc = (d: any, fallback?: number | null): number | null => {
    if (fallback !== undefined && fallback !== null && !isNaN(Number(fallback))) {
      return Number(fallback);
    }
    if (!d) return null;
    if (d.milestoneIndex !== undefined && d.milestoneIndex !== null && !isNaN(Number(d.milestoneIndex))) {
      return Number(d.milestoneIndex);
    }
    if (d.template_data?.milestoneIndex !== undefined && d.template_data?.milestoneIndex !== null && !isNaN(Number(d.template_data.milestoneIndex))) {
      return Number(d.template_data.milestoneIndex);
    }
    if (d.paymentSchedule?.milestoneIndex !== undefined && d.paymentSchedule?.milestoneIndex !== null && !isNaN(Number(d.paymentSchedule.milestoneIndex))) {
      return Number(d.paymentSchedule.milestoneIndex);
    }

    const phaseLabel = d.milestonePhaseLabel || d.template_data?.milestonePhaseLabel || d.paymentSchedule?.milestonePhaseLabel;
    if (phaseLabel) {
      const clean = String(phaseLabel).replace(/[\(\)]/g, '').trim().toUpperCase();
      if (clean in ROMAN_MAP) return ROMAN_MAP[clean];
      const num = parseInt(clean, 10);
      if (!isNaN(num) && num > 0) return num - 1;
    }

    const numStr = d.proformaNumber || d.number || d.documentNumber || d.invoiceNumber || d.subject || d.template_data?.subject || '';
    const match = numStr.match(/\(([IVXLCDM]+|\d+)\)/i);
    if (match) {
      const token = match[1].toUpperCase();
      if (token in ROMAN_MAP) return ROMAN_MAP[token];
      const num = parseInt(token, 10);
      if (!isNaN(num) && num > 0) return num - 1;
    }

    const items = d.items || d.lines || d.template_data?.items || d.template_data?.lines || [];
    if (Array.isArray(items) && items.length === 1) {
      const itemName = items[0]?.name || items[0]?.description || '';
      const lineMatch = itemName.match(/(?:Stage|Phase|Milestone|PI[\s\-_]*\(?)\s*([IVXLCDM]+|\d+)/i);
      if (lineMatch) {
        const token = lineMatch[1].toUpperCase();
        if (token in ROMAN_MAP) return ROMAN_MAP[token];
        const num = parseInt(token, 10);
        if (!isNaN(num) && num > 0) return num - 1;
      }
    }

    return null;
  };

  const draftResolvedIdx = useMemo(() => {
    return resolveMilestoneIndexFromDoc(editingDraft, initialMilestoneIndex);
  }, [editingDraft, initialMilestoneIndex]);

  const [selectedStageIdx, setSelectedStageIdx] = useState<number | null>(
    initialMilestoneIndex !== undefined && initialMilestoneIndex !== null
      ? initialMilestoneIndex
      : (draftResolvedIdx !== null ? draftResolvedIdx : null)
  );
  const [saving, setSaving] = useState(false);

  // Payment Schedule / Milestones state (for the proforma form itself)
  const [paymentType, setPaymentType] = useState<'full_payment' | 'milestone_based'>('full_payment');
  const [advanceRequired, setAdvanceRequired] = useState(false);

  // Choosing Payment Terms drives the milestone stages: master-data terms carry
  // their schedule (30/40/30 etc.), custom terms are parsed for percentages.
  // Only invoked from the terms dropdown / custom input, so quotation and
  // draft auto-fill (which set the state directly) are never overridden.
  const applyPaymentTermToMilestones = useCallback((termName: string) => {
    const schedule = activePaymentTerms.find((t) => t.name === termName)?.schedule;
    let rows: { percentage: number; days: number; description: string }[] | null = null;
    if (Array.isArray(schedule) && schedule.length > 0) {
      rows = schedule
        .map((r: any) => ({
          percentage: Number(r?.percentage),
          days: Number(r?.days) || 0,
          description: r?.description ? String(r.description) : '',
        }))
        .filter((r) => Number.isFinite(r.percentage) && r.percentage > 0);
    } else {
      const percents = parseStagePercents(termName);
      if (percents) rows = percents.map((p) => ({ percentage: p, days: 0, description: '' }));
    }
    if (!rows || rows.length === 0) return;
    const total = rows.reduce((s, r) => s + r.percentage, 0);
    if (Math.abs(total - 100) > 0.5) return;
    if (rows.length === 1 && rows[0].percentage === 100) {
      setPaymentType('full_payment');
      setAdvanceRequired(false);
      return;
    }
    setPaymentType('milestone_based');
    setAdvanceRequired(true);
    const base = new Date(issueDate + 'T00:00:00').getTime();
    setMilestones(
      rows.map((r, i) => ({
        key: uid(),
        label: `Stage ${i + 1}: ${r.description || (i === 0 ? 'Advance' : i === rows.length - 1 ? 'Final Delivery' : `Phase ${i}`)}`,
        percent: r.percentage,
        dueDate: r.days > 0 && !isNaN(base)
          ? new Date(base + r.days * 86400000).toISOString().split('T')[0]
          : '',
      })),
    );
  }, [activePaymentTerms, issueDate]);

  // Document number shown in the milestone payment section (header badge and each stage).
  // Mirrors the source quotation's issued number through the master-data `proforma` config
  // (QT→PI swap). The server re-derives the authoritative number and patches the saved HTML.
  const computedProformaNo = useMemo(() => {
    const raw =
      swapQuotationPrefix((editingDraft as any)?.number) ||
      proformaNumber ||
      swapQuotationPrefix((editingDraft as any)?.proformaNumber) ||
      swapQuotationPrefix((selectedQuotDoc as any)?.proformaNumber) ||
      swapQuotationPrefix((selectedQuotDoc as any)?.number) ||
      swapQuotationPrefix(initialQuotationNumber) ||
      '';
    return raw
      .replace(/\s*\((?:PI|TI)?\s*\([IVXLCDM]+\)\)/gi, '')
      .replace(/\s*\((?:PI|TI)\)/gi, '')
      .replace(/\s*\([IVXLCDM]+\)/gi, '')
      .trim();
  }, [
    selectedQuotDoc?.id ?? selectedQuotDoc?._id,
    (editingDraft as any)?.number,
    proformaNumber,
    (editingDraft as any)?.proformaNumber,
    initialQuotationNumber,
  ]);

  const effectiveStageIdx = selectedStageIdx !== null
    ? selectedStageIdx
    : (initialMilestoneIndex !== undefined && initialMilestoneIndex !== null
        ? initialMilestoneIndex
        : draftResolvedIdx);
  const isSpecificMilestoneMode = effectiveStageIdx !== null && effectiveStageIdx !== undefined;

  useEffect(() => {
    smoothScrollToTop();
  }, []);

  const defaultTerms = useMemo(() => {
    const active = (termConditions || []).filter(t => t.isActive !== false && !(t as any).deletedAt);
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return (a.displayOrder || 0) - (b.displayOrder || 0);
    });
    return sorted.map(t => t.content).filter(Boolean).join('\n\n');
  }, [termConditions]);

  const defaultTermsArray = useMemo(() => {
    const active = (termConditions || []).filter(t => t.isActive !== false && !(t as any).deletedAt);
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return (a.displayOrder || 0) - (b.displayOrder || 0);
    });
    const linesArr: string[] = [];
    sorted.forEach(t => {
      const text = t.content || (t as any).description || t.title;
      if (text) {
        text.split('\n').map((s: string) => s.trim()).filter(Boolean).forEach((s: string) => linesArr.push(s));
      }
    });
    return linesArr;
  }, [termConditions]);

  const defaultAssumptions = useMemo(() => {
    const active = (assumptionsExclusions || []).filter(a =>
      a.isActive !== false && !(a as any).deletedAt && String(a.category || '').toUpperCase() === 'ASSUMPTION'
    );
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0);
    });
    return sorted.map(a => '• ' + (a.description || (a as any).content || a.title)).filter(Boolean).join('\n');
  }, [assumptionsExclusions]);

  const defaultExclusions = useMemo(() => {
    const active = (assumptionsExclusions || []).filter(a =>
      a.isActive !== false && !(a as any).deletedAt && String(a.category || '').toUpperCase() === 'EXCLUSION'
    );
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0);
    });
    return sorted.map(a => '• ' + (a.description || (a as any).content || a.title)).filter(Boolean).join('\n');
  }, [assumptionsExclusions]);

  const defaultAssumptionsArray = useMemo(() => {
    const active = (assumptionsExclusions || []).filter(a => a.isActive !== false && !(a as any).deletedAt);
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0);
    });
    return sorted.map(a => {
      const isExcl = String(a.category || '').toUpperCase() === 'EXCLUSION';
      const prefix = isExcl ? 'EXCLUSION: ' : '';
      const text = a.description || (a as any).content || a.title || '';
      return prefix + text;
    }).filter(Boolean);
  }, [assumptionsExclusions]);
  const defaultDeclaration = useMemo(() => declarations.find((d: any) => d.isDefault)?.content || '', [declarations]);
  const defaultBankDetail = useMemo(() => bankDetails.find((b: any) => b.isDefault && b.isActive), [bankDetails]);
  const companyRegisteredAddress = useMemo(() => {
    const addr = (companyProfile as any)?.registeredAddress;
    if (!addr) return '';
    return [addr.line1, addr.line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ');
  }, [companyProfile]);

  // Initialize business/sender details from company profile
  useEffect(() => {
    if (!companyProfile) return;
      setBusinessName(prev => prev || (companyProfile as any)?.legalName || (companyProfile as any)?.name || '');
      setBusinessAddress(prev => prev || companyRegisteredAddress || '');
      setBusinessTagline(prev => prev || (companyProfile as any)?.tagline || (companyProfile as any)?.industry || '');
      setBusinessGstin(prev => prev || (companyProfile as any)?.gstin || companyProfile.gst || '');
      setBusinessPan(prev => prev || companyProfile.pan || '');
      setBusinessPhone(prev => prev || companyProfile.phone || '');
      setBusinessEmail(prev => prev || (companyProfile as any)?.officialEmail || companyProfile.email || '');
      setBusinessWeb(prev => prev || companyProfile.website || '');
  }, [companyProfile, companyRegisteredAddress]);

  // Initialize bank details from default or company profile
  useEffect(() => {
    if (bankDetailId) return;
    const def = defaultBankDetail || bankDetails.find(b => b.isActive);
    if (def) {
      setBankDetailId(def.id);
      setBankAccName(def.accountName || '');
      setBankAccNo(def.accountNumber || '');
      setBankName(def.bankName || '');
      setBankBranch(def.branch || '');
      setBankIfsc(def.ifsc || '');
      setBankUpi(def.upi || '');
    }
  }, [bankDetails, bankDetailId, defaultBankDetail]);

  // Sync master data defaults for assumptions, declarations
  useEffect(() => {
    if (editingDraft) return;
    if (assumptionsSectionEnabled && assumptionsList.length === 0 && defaultAssumptionsArray.length > 0) {
      setAssumptionsList([...defaultAssumptionsArray]);
      setAssumptions(defaultAssumptions + (defaultExclusions ? '\n' + defaultExclusions : ''));
    }
    if (!declaration && defaultDeclaration) {
      setDeclaration(defaultDeclaration);
      const matched = declarations.find((d: any) => d.content === defaultDeclaration);
      if (matched) setDeclarationId(matched.id);
    }
  }, [editingDraft, assumptionsSectionEnabled, assumptionsList.length, defaultAssumptionsArray, defaultAssumptions, defaultExclusions, declaration, defaultDeclaration, declarations]);

  useEffect(() => {
    if (editingDraft) return;
    const dealOrLead = fromDeal || fromLead;
    if (!dealOrLead && !initialServices?.length && !initialClientName && !initialAmount) return;

    if (dealOrLead) {
      setLinkedDeal(dealOrLead);
    }

    const cName = initialClientName || dealOrLead?.clientName || dealOrLead?.client || dealOrLead?.company || dealOrLead?.name || dealOrLead?.businessName || '';
    const cEmail = initialClientEmail || dealOrLead?.contactEmail || dealOrLead?.email || dealOrLead?.clientEmail || '';
    const cPhone = initialClientPhone || dealOrLead?.contactPhone || dealOrLead?.phone || dealOrLead?.clientPhone || '';
    const cPerson = dealOrLead?.contactPerson || dealOrLead?.contact_name || dealOrLead?.name || '';
    let cAddress = dealOrLead?.clientAddress || dealOrLead?.address || dealOrLead?.billingAddress || dealOrLead?.location || '';
    let cGstin = dealOrLead?.clientGstin || dealOrLead?.gstin || '';

    const rawVal = initialAmount ?? dealOrLead?.value ?? dealOrLead?.amount ?? 0;
    const value = rawVal && !isNaN(Number(rawVal)) ? Number(rawVal) : 0;
    const title = initialSubject || dealOrLead?.title || (cName ? `Proforma Invoice for ${cName}` : '');
    const desc = initialNotes || dealOrLead?.description || dealOrLead?.notes || '';

    if (!cAddress && (dealOrLead?.notes || desc)) {
      const noteText = String(dealOrLead?.notes || desc);
      const am = noteText.match(/(?:Address|Billing Address|Location):\s*([^,\n\)]+)/i);
      if (am) cAddress = am[1].trim();
    }
    if (!cGstin && (dealOrLead?.notes || desc)) {
      const noteText = String(dealOrLead?.notes || desc);
      const gm = noteText.match(/(?:GSTIN|GST|Tax ID):\s*([A-Z0-9]{15})/i);
      if (gm) cGstin = gm[1].trim();
    }

    const rawSrvs: string[] = Array.isArray(initialServices) && initialServices.length > 0
      ? initialServices
      : (Array.isArray(dealOrLead?.services) && dealOrLead.services.length > 0
        ? dealOrLead.services
        : (dealOrLead?.service_required ? dealOrLead.service_required.split(',').map((s: string) => s.trim()).filter(Boolean)
        : (dealOrLead?.serviceRequired ? dealOrLead.serviceRequired.split(',').map((s: string) => s.trim()).filter(Boolean) : [])));

    if (cName) setClientName(cName);
    if (cEmail) setClientEmail(cEmail);
    if (cPhone) setClientPhone(cPhone);
    if (cPerson) setContactPerson(cPerson);
    if (cAddress) setClientAddress(cAddress);
    if (cGstin) setClientGstin(cGstin);
    if (title) setSubject(title);
    if (desc) setIntroText(desc);

    if (rawSrvs.length > 0) {
      // Build master-data lookup (name → ProductService)
      const mdMap: Record<string, ProductServiceData> = {}
      for (const ps of masterServices) {
        if (ps.name) mdMap[ps.name.toLowerCase()] = ps
      }

      const generatedLines = rawSrvs.map(srvName => {
        // Try master data first, fall back to hardcoded constants
        const md = mdMap[srvName.toLowerCase()]
        const fallback = allMasterServices.find(x => x.name.toLowerCase() === srvName.toLowerCase());
        const s = md || fallback;
        const itemRate = (value > 0 && rawSrvs.length === 1)
          ? value
          : ((md?.defaultRate || fallback?.defaultRate) || (value > 0 ? Math.round(value / rawSrvs.length) : 25000));

        const sf = serviceLineFields(s?.name || srvName, allMasterServices, hsnSacCodes);
        return {
          key: uid(),
          name: s?.name || srvName,
          description: s?.name || srvName,
          hsnSac: s?.hsnSac || '998314',
          quantity: 1,
          unit: (s as any)?.defaultUnit || s?.unit || 'Nos',
          rate: itemRate,
          discountPercent: 0,
          taxRate: sf.gstRate,
          taxMasterId: sf.taxMasterId,
        };
      });
      setLines(generatedLines);
    } else if (value > 0) {
      setLines([{
        key: uid(),
        name: 'Service',
        description: 'Service',
        hsnSac: '998314',
        quantity: 1,
        unit: 'project',
        rate: value,
        discountPercent: 0,
        taxRate: 18,
      }]);
    }
  }, [fromDeal, fromLead, initialServices, initialClientName, initialClientEmail, initialClientPhone, initialAmount, initialSubject, initialNotes, editingDraft]);

  const populateMilestonesFromStages = (stages: any[]) => {
    if (Array.isArray(stages) && stages.length > 0) {
      setMilestones(
        stages.map((m: any, i: number) => ({
          key: uid(),
          label: m.label || m.name || `Phase ${m.milestoneNumber || i + 1}`,
          percent: Number(m.percent ?? m.percentage ?? m.pct ?? 0) || 0,
          dueDate: m.dueDate ? (String(m.dueDate).includes('T') ? String(m.dueDate).split("T")[0] : String(m.dueDate)) : (m.due || ""),
        }))
      );
    } else {
      setMilestones([]);
    }
  };

  const handleQuotationSelect = (id: string, pool?: InvoiceRequest[], linkedSchedule?: any) => {
    setSelectedQuotId(id);
    if (!id) {
      setSelectedQuotDoc(null);
      setClientName("");
      setClientEmail("");
      setClientPhone("");
      setClientAddress("");
      setClientGstin("");
      setClientState("");
      setContactPerson("");
      setContactDesignation("");
      setPoNumber("");
      setSubject("");
      setIntroText("");
      setNotes("");
      setTerms("");
      setLines([emptyLine()]);
      setMilestones([]);
      setGstEnabled(true);
      setReverseCharge(false);
      setPlaceOfSupply("");
      return;
    }
    const list = pool || quotations;
    const q = list.find(r => r.id === id || (r as any)._id === id) || (selectedQuotDoc?._id === id || selectedQuotDoc?.id === id ? selectedQuotDoc : null);
    if (!q) return;

    setSelectedQuotDoc(q);

    // Resolve deal if present
    const dealId = (q as any).sourceDealId || (q as any).dealId || (q as any).clientId;
    let dealInfo = fromDeal;
    if (dealId && (!dealInfo || (dealInfo.id !== dealId && dealInfo._id !== dealId))) {
      api.get(`/api/deals/${dealId}`).then(dRes => {
        const d = dRes.data?.deal || dRes.data?.data || dRes.data;
        if (d) {
          setLinkedDeal(d);
          if (d.clientAddress || d.address || d.billingAddress) {
            setClientAddress(prev => prev || d.clientAddress || d.address || d.billingAddress || '');
          }
          if (d.clientGstin || d.gstin) {
            setClientGstin(prev => prev || d.clientGstin || d.gstin || '');
          }
          if (d.contactPerson) {
            setContactPerson(prev => prev || d.contactPerson || '');
          }
          if (d.contactEmail || d.email) {
            setClientEmail(prev => prev || d.contactEmail || d.email || '');
          }
          if (d.contactPhone || d.phone) {
            setClientPhone(prev => prev || d.contactPhone || d.phone || '');
          }
        }
      }).catch(() => {});
    } else if (dealInfo) {
      setLinkedDeal(dealInfo);
    }

    const resolvedClientName = q.clientName || (q as any).customerName || (q as any).template_data?.clientName || dealInfo?.clientName || dealInfo?.client || dealInfo?.company || "";
    setClientName(resolvedClientName);
    setClientEmail((q as any).clientEmail || (q as any).customerEmail || (q as any).template_data?.attnEmail || (q as any).template_data?.clientEmail || (q as any).template_data?.customerEmail || dealInfo?.contactEmail || dealInfo?.email || "");
    setClientPhone((q as any).clientPhone || (q as any).customerPhone || (q as any).customerContact || (q as any).template_data?.attnPhone || (q as any).template_data?.clientPhone || (q as any).template_data?.customerPhone || dealInfo?.contactPhone || dealInfo?.phone || "");
    
    let resolvedAddress = (q as any).clientAddress || (q as any).customerAddress || (q as any).billingAddress || (q as any).template_data?.clientAddr || (q as any).template_data?.clientAddress || dealInfo?.clientAddress || dealInfo?.address || dealInfo?.billingAddress || "";
    let resolvedGstin = (q as any).clientGstin || (q as any).customerGstin || (q as any).template_data?.clientGstin || dealInfo?.clientGstin || dealInfo?.gstin || "";

    const noteText = String(q.notes || (q as any).template_data?.notes || dealInfo?.notes || "");
    if (!resolvedAddress && noteText) {
      const am = noteText.match(/(?:Address|Billing Address|Location):\s*([^,\n\)]+)/i);
      if (am) resolvedAddress = am[1].trim();
    }
    if (!resolvedGstin && noteText) {
      const gm = noteText.match(/(?:GSTIN|GST|Tax ID):\s*([A-Z0-9]{15})/i);
      if (gm) resolvedGstin = gm[1].trim();
    }

    setClientAddress(resolvedAddress);
    setClientGstin(resolvedGstin);
    setClientState((q as any).clientState || (q as any).customerState || (q as any).placeOfSupply || (q as any).template_data?.clientState || "");
    setContactPerson((q as any).contactPerson || (q as any).template_data?.contactPerson || dealInfo?.contactPerson || dealInfo?.name || "");
    setContactDesignation((q as any).contactDesignation || (q as any).template_data?.contactDesignation || "");
    setPoNumber((q as any).poNumber || (q as any).referenceNo || (q as any).template_data?.referenceNo || "");
    setSubject((q as any).subject || (q as any).template_data?.subject || (dealInfo?.title ? `Proforma Invoice for ${dealInfo.title}` : (resolvedClientName ? `Proforma Invoice for ${resolvedClientName}` : "")));
    setIntroText((q as any).introText || (q as any).template_data?.introText || dealInfo?.notes || "");
    setNotes(q.notes || (Array.isArray((q as any).template_data?.notes) ? (q as any).template_data.notes.join('\n') : (dealInfo?.notes || "")));
    setTerms((q as any).terms || (Array.isArray((q as any).template_data?.terms) ? (q as any).template_data.terms.join('\n') : ""));
    setTermsList((q as any).termsList || (Array.isArray((q as any).template_data?.termsList) ? (q as any).template_data.termsList : ((q as any).terms ? (q as any).terms.split('\n').filter(Boolean) : [])));
    setAssumptions((q as any).assumptions || (Array.isArray((q as any).template_data?.assumptions) ? (q as any).template_data.assumptions.join('\n') : ""));
    setAssumptionsList((q as any).assumptionsList || (Array.isArray((q as any).template_data?.assumptionsList) ? (q as any).template_data.assumptionsList : ((q as any).assumptions ? (q as any).assumptions.split('\n').filter(Boolean) : [])));
    setDeclaration((q as any).declaration || (q as any).template_data?.declaration || '');
    setDeclarationId((q as any).declarationId || (q as any).template_data?.declarationId || '');
    setPreparedBy((q as any).preparedBy || (q as any).template_data?.preparedBy || (q as any).requestedByName || '');
    setCurrency((q as any).currency || 'INR');
    setHeaderDiscount(Number((q as any).headerDiscount ?? (q as any).template_data?.headerDiscount ?? 0));
    setHeaderDiscountType(((q as any).headerDiscountType ?? (q as any).template_data?.headerDiscountType ?? 'percent') as 'percent' | 'fixed');
    setAdditionalCharges(Number((q as any).additionalCharges ?? (q as any).template_data?.additionalCharges ?? 0));
    setAdditionalChargesLabel((q as any).additionalChargesLabel || (q as any).template_data?.additionalChargesLabel || '');
    setClientId((q as any).clientId || (q as any).template_data?.clientId || '');
    // GST configuration — always default to enabled for proforma invoices
    setGstEnabled(true);
    setReverseCharge(Boolean((q as any).reverseCharge ?? (q as any).template_data?.reverseCharge));
    setPlaceOfSupply((q as any).placeOfSupply || (q as any).template_data?.placeOfSupply || (q as any).clientState || (q as any).customerState || "");
    // Business / sender block and bank details — prefer the quotation's saved values over company-profile defaults
    const qtd = (q as any).template_data || {};
    if ((q as any).businessName || qtd.businessName) setBusinessName((q as any).businessName || qtd.businessName);
    if ((q as any).businessAddress || qtd.businessAddress) setBusinessAddress((q as any).businessAddress || qtd.businessAddress);
    if ((q as any).businessGstin || qtd.businessGstin) setBusinessGstin((q as any).businessGstin || qtd.businessGstin);
    if ((q as any).businessPan || qtd.businessPan) setBusinessPan((q as any).businessPan || qtd.businessPan);
    if ((q as any).businessPhone || qtd.businessPhone) setBusinessPhone((q as any).businessPhone || qtd.businessPhone);
    if ((q as any).businessEmail || qtd.businessEmail) setBusinessEmail((q as any).businessEmail || qtd.businessEmail);
    if ((q as any).businessWeb || qtd.businessWeb) setBusinessWeb((q as any).businessWeb || qtd.businessWeb);
    if ((q as any).bankDetailId || qtd.bankDetailId) setBankDetailId((q as any).bankDetailId || qtd.bankDetailId);
    if ((q as any).bankAccName || qtd.bankAccName) setBankAccName((q as any).bankAccName || qtd.bankAccName);
    if ((q as any).bankAccNo || qtd.bankAccNo) setBankAccNo((q as any).bankAccNo || qtd.bankAccNo);
    if ((q as any).bankName || qtd.bankName) setBankName((q as any).bankName || qtd.bankName);
    if ((q as any).bankBranch || qtd.bankBranch) setBankBranch((q as any).bankBranch || qtd.bankBranch);
    if ((q as any).bankIfsc || qtd.bankIfsc) setBankIfsc((q as any).bankIfsc || qtd.bankIfsc);
    if ((q as any).bankUpi || qtd.bankUpi) setBankUpi((q as any).bankUpi || qtd.bankUpi);
    setBusinessTagline((q as any).template_data?.businessTag || (q as any).template_data?.businessTagline || '');
    // Shipping fields
    setShippingEnabled(Boolean((q as any).shippingEnabled));
    setShippingName((q as any).shippingName || (q as any).template_data?.shippingName || '');
    setShippingAddress((q as any).shippingAddress || (q as any).template_data?.shippingAddress || '');
    setShippingCity((q as any).shippingCity || (q as any).template_data?.shippingCity || '');
    setShippingState((q as any).shippingState || (q as any).template_data?.shippingState || '');
    setShippingPincode((q as any).shippingPincode || (q as any).template_data?.shippingPincode || '');
    // Display options
    const dopts = (q as any).displayOptions || (q as any).template_data?.displayOptions || {};
    setShowHsnColumn(dopts.showHsnColumn !== false);
    setShowTaxSummary(dopts.showTaxSummary !== false);
    setShowSkuColumn(Boolean(dopts.showSkuColumn));
    setShowSerialNumbers(Boolean(dopts.showSerialNumbers));
    setShowThumbnails(Boolean(dopts.showThumbnails));
    setShowDescFullWidth(Boolean(dopts.showDescFullWidth));
    setHideSubtotalForGroups(Boolean(dopts.hideSubtotalForGroups));
    setHidePlaceOfSupply(Boolean(dopts.hidePlaceOfSupply));
    setDisplayUnit(dopts.displayUnit || 'short');

    if ((q as any).paymentTerms || (q as any).template_data?.paymentTerms) {
      setPaymentTerms((q as any).paymentTerms || (q as any).template_data?.paymentTerms);
    }
    if ((q as any).validUntil || (q as any).validTillDate) {
      setValidUntil(String((q as any).validUntil || (q as any).validTillDate).split("T")[0]);
    }

    const isPlaceholderLine = (item: any) => {
      if (!item) return false;
      const str = String(item.name || item.description || '').toLowerCase();
      return str.includes('full 100% payment on delivery') || str.includes('payment on delivery / order for quotation');
    };

    // Preserve existing draft lines during revision/edit only if they are real items, not default placeholders
    const draftItemsList = (editingDraft as any)?.items || (editingDraft as any)?.lines || (editingDraft as any)?.template_data?.items || (editingDraft as any)?.template_data?.lines || [];
    const hasOnlyPlaceholderItems = Array.isArray(draftItemsList) && draftItemsList.length > 0 && draftItemsList.every(isPlaceholderLine);
    const isDraftWithLines = Boolean(editingDraft && draftItemsList.length > 0 && !hasOnlyPlaceholderItems);

    if (!isDraftWithLines) {
      const rawItems = q.items || (q as any).lines || (q as any).template_data?.items || (q as any).template_data?.lines;
      const validRawItems = Array.isArray(rawItems) ? rawItems.filter(it => !isPlaceholderLine(it)) : [];
      if (validRawItems.length > 0) {
        setLines(validRawItems.map(mapIRLine));
      } else if (rawItems && rawItems.length > 0 && !hasOnlyPlaceholderItems) {
        setLines(rawItems.map(mapIRLine));
      } else {
        const rawServices: string[] = Array.isArray((q as any).services) && (q as any).services.length > 0
          ? (q as any).services
          : ((q as any).service_required ? (q as any).service_required.split(',').map((s: string) => s.trim()).filter(Boolean)
          : ((q as any).template_data?.services ? (q as any).template_data.services
          : []));
        const qVal = Number((q as any).value ?? (q as any).total ?? (q as any).grandTotal ?? (q as any).subtotal ?? 0);

        if (rawServices.length > 0) {
          // Build master-data lookup (name → ProductService)
          const mdMap: Record<string, ProductServiceData> = {}
          for (const ps of masterServices) {
            if (ps.name) mdMap[ps.name.toLowerCase()] = ps
          }

          setLines(rawServices.map((srvName: string) => {
            const md = mdMap[srvName.toLowerCase()]
            const fallback = allMasterServices.find(x => x.name.toLowerCase() === srvName.toLowerCase());
        const s = md || fallback;
        const itemRate = (qVal > 0 && rawServices.length === 1)
          ? qVal
          : (md?.defaultRate || fallback?.defaultRate || (qVal > 0 ? Math.round(qVal / rawServices.length) : 0));

            const sf = serviceLineFields(s?.name || srvName, allMasterServices, hsnSacCodes);
            return {
              key: uid(),
              name: s?.name || srvName,
              description: s?.name || srvName,
              hsnSac: s?.hsnSac || "998314",
              quantity: 1,
              unit: (s as any)?.defaultUnit || s?.unit || 'Nos',
              rate: itemRate,
              discountPercent: 0,
              taxRate: sf.gstRate,
              taxMasterId: sf.taxMasterId,
            };
          }));
        } else if (qVal > 0) {
          setLines([{
            key: uid(),
            name: q.subject || 'Commercial Services',
            description: q.introText || q.subject || 'Service Scope & Commercials',
            hsnSac: '998314',
            quantity: 1,
            unit: 'Nos',
            rate: qVal,
            discountPercent: 0,
            taxRate: 18,
          }]);
        } else {
          setLines([emptyLine()]);
        }
      }
    }

    const currentSched = linkedSchedule || schedDoc || (q as any).paymentSchedule;
    const qMilestones = (Array.isArray((q as any).milestones) && (q as any).milestones.length > 0)
      ? (q as any).milestones
      : (Array.isArray((q as any).paymentSchedule?.milestones) && (q as any).paymentSchedule.milestones.length > 0)
        ? (q as any).paymentSchedule.milestones
        : (Array.isArray((q as any).template_data?.milestones) && (q as any).template_data.milestones.length > 0)
          ? (q as any).template_data.milestones
          : (Array.isArray(currentSched?.milestones) && currentSched.milestones.length > 0)
            ? currentSched.milestones
            : [];
    const qt = (q as any).paymentType || (q as any).template_data?.paymentType || currentSched?.paymentType;
    const isMilestoneBased = qt === 'milestone_based' || (qt !== 'full_payment' && qMilestones.length > 0) || qMilestones.length > 1;
    setQuotPaymentType(isMilestoneBased ? 'milestone_based' : 'full_payment');
    setPaymentType(isMilestoneBased ? 'milestone_based' : 'full_payment');
    setAdvanceRequired(isMilestoneBased);
    if (isMilestoneBased && qMilestones.length > 0) {
      setMilestones(qMilestones.map((m: any, idx: number) => ({
        key: uid(),
        label: m.label || m.name || `Stage ${idx + 1}`,
        percent: Number(m.percent || m.percentage || (100 / qMilestones.length)),
        dueDate: m.dueDate ? String(m.dueDate).split('T')[0] : '',
        triggerType: m.triggerType,
        description: m.description || '',
        daysFromApproval: Number(m.daysFromApproval || 0),
      })));
    } else {
      setMilestones([]);
    }
    setSelectedStageIdx(null);
  };

  // When in edit mode, populate all form fields from the existing draft
  useEffect(() => {
    if (!editingDraft) return;
    const d = editingDraft as any;
    setClientName(d.clientName || d.customerName || d.template_data?.clientName || '');
    setClientEmail(d.clientEmail || d.customerEmail || d.template_data?.clientEmail || d.template_data?.attnEmail || '');
    setClientPhone(d.clientPhone || d.customerPhone || d.customerContact || d.template_data?.clientPhone || d.template_data?.attnPhone || '');
    setClientAddress(d.clientAddress || d.customerAddress || d.billingAddress || d.template_data?.clientAddr || d.template_data?.clientAddress || '');
    setClientGstin(d.clientGstin || d.customerGstin || d.template_data?.clientGstin || '');
    setClientState(d.clientState || d.customerState || d.placeOfSupply || d.template_data?.clientState || '');
    setContactPerson(d.contactPerson || d.template_data?.contactPerson || '');
    setContactDesignation(d.contactDesignation || d.template_data?.contactDesignation || '');
    setPoNumber(d.poNumber || d.referenceNo || d.template_data?.referenceNo || '');
    setSubject(d.subject || d.template_data?.subject || '');
    setIntroText(d.introText || d.template_data?.introText || '');
    setNotes(d.notes || (Array.isArray(d.template_data?.notes) ? d.template_data.notes.join('\n') : (d.template_data?.notes || '')));
    setTerms(d.terms || (Array.isArray(d.template_data?.terms) ? d.template_data.terms.join('\n') : (d.template_data?.terms || '')));
    setTermsList(d.termsList || (Array.isArray(d.template_data?.termsList) ? d.template_data.termsList : (d.terms ? d.terms.split('\n').filter(Boolean) : [])));
    setAssumptions(d.assumptions || (Array.isArray(d.template_data?.assumptions) ? d.template_data.assumptions.join('\n') : ''));
    setAssumptionsList(d.assumptionsList || (Array.isArray(d.template_data?.assumptionsList) ? d.template_data.assumptionsList : (d.assumptions ? d.assumptions.split('\n').filter(Boolean) : [])));
    setDeclaration(d.declaration || d.template_data?.declaration || '');
    setDeclarationId(d.declarationId || d.template_data?.declarationId || '');
    setBusinessName(d.businessName || d.template_data?.businessName || (companyProfile as any)?.legalName || (companyProfile as any)?.name || '');
    setBusinessAddress(d.businessAddress || d.template_data?.businessAddress || companyRegisteredAddress || '');
    setBusinessGstin(d.businessGstin || d.template_data?.businessGstin || (companyProfile as any)?.gstin || companyProfile?.gst || '');
    setBusinessPan(d.businessPan || d.template_data?.businessPan || companyProfile?.pan || '');
    setBusinessPhone(d.businessPhone || d.template_data?.businessPhone || companyProfile?.phone || '');
    setBusinessEmail(d.businessEmail || d.template_data?.businessEmail || (companyProfile as any)?.officialEmail || companyProfile?.email || '');
    setBusinessWeb(d.businessWeb || d.template_data?.businessWeb || companyProfile?.website || '');
    setGstEnabled(d.gstEnabled !== undefined ? d.gstEnabled : true);
    setPlaceOfSupply(d.placeOfSupply || d.clientState || d.template_data?.placeOfSupply || '');
    setReverseCharge(Boolean(d.reverseCharge));
    setBankDetailId(d.bankDetailId || '');
    setBankAccName(d.bankAccName || d.template_data?.bankAccName || '');
    setBankAccNo(d.bankAccNo || d.template_data?.bankAccNo || '');
    setBankName(d.bankName || d.template_data?.bankName || '');
    setBankBranch(d.bankBranch || d.template_data?.bankBranch || '');
    setBankIfsc(d.bankIfsc || d.template_data?.bankIfsc || '');
    setBankUpi(d.bankUpi || d.template_data?.bankUpi || '');
    setPreparedBy(d.preparedBy || d.template_data?.preparedBy || '');
    setCurrency(d.currency || 'INR');
    setHeaderDiscount(Number(d.headerDiscount ?? d.template_data?.headerDiscount ?? 0));
    setHeaderDiscountType(((d.headerDiscountType ?? d.template_data?.headerDiscountType ?? 'percent') as 'percent' | 'fixed'));
    setAdditionalCharges(Number(d.additionalCharges ?? d.template_data?.additionalCharges ?? 0));
    setAdditionalChargesLabel(d.additionalChargesLabel || d.template_data?.additionalChargesLabel || '');
    setClientId(d.clientId || d.template_data?.clientId || '');
    setBusinessTagline(d.template_data?.businessTag || d.template_data?.businessTagline || '');
    setShippingEnabled(Boolean(d.shippingEnabled));
    setShippingName(d.shippingName || d.template_data?.shippingName || '');
    setShippingAddress(d.shippingAddress || d.template_data?.shippingAddress || '');
    setShippingCity(d.shippingCity || d.template_data?.shippingCity || '');
    setShippingState(d.shippingState || d.template_data?.shippingState || '');
    setShippingPincode(d.shippingPincode || d.template_data?.shippingPincode || '');
    const eDopts = d.displayOptions || d.template_data?.displayOptions || {};
    setShowHsnColumn(eDopts.showHsnColumn !== false);
    setShowTaxSummary(eDopts.showTaxSummary !== false);
    setShowSkuColumn(Boolean(eDopts.showSkuColumn));
    setShowSerialNumbers(Boolean(eDopts.showSerialNumbers));
    setShowThumbnails(Boolean(eDopts.showThumbnails));
    setShowDescFullWidth(Boolean(eDopts.showDescFullWidth));
    setHideSubtotalForGroups(Boolean(eDopts.hideSubtotalForGroups));
    setHidePlaceOfSupply(Boolean(eDopts.hidePlaceOfSupply));
    setDisplayUnit(eDopts.displayUnit || 'short');
    if (d.date || d.quotationDate) setIssueDate(String(d.date || d.quotationDate).split('T')[0]);
    if (d.validUntil || d.validTillDate) setValidUntil(String(d.validUntil || d.validTillDate).split('T')[0]);
    if (d.paymentTerms || d.template_data?.paymentTerms) setPaymentTerms(d.paymentTerms || d.template_data?.paymentTerms);
    if (d.sourceQuotationId || d.quotationId) setSelectedQuotId(d.sourceQuotationId || d.quotationId);
    const isPlaceholderLine = (item: any) => {
      if (!item) return false;
      const str = String(item.name || item.description || '').toLowerCase();
      return str.includes('full 100% payment on delivery') || str.includes('payment on delivery / order for quotation');
    };
    const rawItems = d.items || d.lines || d.template_data?.items || d.template_data?.lines;
    // Older proformas kept per-line notes only inside template_data.items — backfill
    // by index so editing and re-saving doesn't silently drop them.
    const templateItems = Array.isArray(d.template_data?.items) ? d.template_data.items : [];
    const rawItemsWithNotes = (Array.isArray(rawItems) && templateItems.length > 0)
      ? rawItems.map((it: any, i: number) => (
          !String(it?.notes || '').trim() && String(templateItems[i]?.notes || '').trim()
            ? { ...it, notes: templateItems[i].notes }
            : it
        ))
      : rawItems;
    const validDraftItems = Array.isArray(rawItemsWithNotes) ? rawItemsWithNotes.filter(it => !isPlaceholderLine(it)) : [];
    if (validDraftItems.length > 0) {
      setLines(validDraftItems.map(mapIRLine));
    }
    const msList = d.milestones || d.template_data?.milestones || d.paymentSchedule?.milestones;
    const editPaymentType = d.template_data?.paymentType || d.paymentType;
    const editIsMilestone = editPaymentType === 'milestone_based' || (!editPaymentType && Array.isArray(msList) && msList.length > 0);
    setQuotPaymentType(editIsMilestone ? 'milestone_based' : 'full_payment');
    setPaymentType(editIsMilestone ? 'milestone_based' : 'full_payment');
    setAdvanceRequired(editIsMilestone);
    if (editIsMilestone && Array.isArray(msList) && msList.length > 0) {
      setMilestones(msList.map((m: any, idx: number) => ({
        key: uid(),
        label: m.label || m.name || `Stage ${idx + 1}`,
        percent: Number(m.percent || m.percentage || 0),
        dueDate: m.dueDate ? String(m.dueDate).split('T')[0] : '',
        triggerType: m.triggerType,
        description: m.description || '',
        daysFromApproval: Number(m.daysFromApproval || 0),
      })));
    } else {
      setMilestones([]);
    }
    setSelectedStageIdx(null);
  }, [editingDraft, initialMilestoneIndex]);

  useEffect(() => {
    (async () => {
      try {
        let initialDirectDoc: any = null;
        let scheduleData: any = null;

        if (initialScheduleId) {
          try {
            const schRes = await api.get(`/api/accounting/payment-schedules/${initialScheduleId}`);
            scheduleData = schRes.data?.data || schRes.data?.schedule || schRes.data;
            if (scheduleData) setSchedDoc(scheduleData);
          } catch { /* fallback */ }
        }

        if (initialQuotationId) {
          try {
            const directRes = await api.get(`/api/invoice-requests/${initialQuotationId}`);
            initialDirectDoc = directRes.data?.request || directRes.data?.data || directRes.data;
          } catch { /* fallback to list query */ }
        }

        if (!initialDirectDoc && initialQuotationNumber) {
          try {
            const qnRes = await api.get(`/api/invoice-requests?number=${encodeURIComponent(initialQuotationNumber)}`);
            const qnList = qnRes.data?.requests || qnRes.data?.items || [];
            if (qnList.length > 0) initialDirectDoc = qnList[0];
          } catch { /* fallback */ }
        }

        if (!scheduleData && (initialQuotationId || initialQuotationNumber || initialDirectDoc)) {
          try {
            const qId = initialQuotationId || initialDirectDoc?.id || initialDirectDoc?._id;
            const qNo = initialQuotationNumber || initialDirectDoc?.number;
            const schRes = await api.get('/api/accounting/payment-schedules?limit=100');
            const allSchedules = schRes.data?.data?.items || schRes.data?.items || schRes.data || [];
            if (Array.isArray(allSchedules)) {
              scheduleData = allSchedules.find((s: any) =>
                (qId && (s.quotationId === qId || String(s.quotationId) === String(qId))) ||
                (qNo && s.quotationNumber === qNo)
              );
              if (scheduleData) setSchedDoc(scheduleData);
            }
          } catch { /* fallback */ }
        }

        // Fetch quotations (invoice requests)
        const invReqsRes = await api.get('/api/invoice-requests?requestType=quotation&limit=100').catch(() => null);

        const invReqsList = invReqsRes ? (invReqsRes.data?.requests || invReqsRes.data?.items || invReqsRes.data || []) : [];

        // Spread the raw record first so no stored field (currency, validUntil, headerDiscount,
        // shipping, displayOptions, GST config, bank, business block…) is dropped; the explicit
        // keys below only override what needs normalizing.
        const normalizedInvReqs = (Array.isArray(invReqsList) ? invReqsList : []).map((r: any) => ({
          ...r,
          id: r._id || r.id,
          _id: r._id || r.id,
          number: r.number || r.documentNumber || '',
          requestType: 'quotation',
          status: r.status || 'approved',
          clientName: r.clientName || r.customerName || (r as any).template_data?.clientName || 'Client Company',
          clientEmail: r.clientEmail || r.customerEmail || (r as any).template_data?.clientEmail || '',
          clientPhone: r.clientPhone || r.customerPhone || (r as any).template_data?.clientPhone || '',
          clientAddress: r.clientAddress || r.customerAddress || (r as any).template_data?.clientAddr || '',
          clientGstin: r.clientGstin || r.customerGstin || (r as any).template_data?.clientGstin || '',
          clientState: r.clientState || r.customerState || (r as any).template_data?.clientState || '',
          contactPerson: r.contactPerson || (r as any).template_data?.contactPerson || '',
          contactDesignation: r.contactDesignation || (r as any).template_data?.contactDesignation || '',
          poNumber: r.poNumber || r.referenceNo || '',
          subject: r.subject || (r as any).template_data?.subject || '',
          introText: r.introText || (r as any).template_data?.introText || '',
          subtotal: r.subtotal || 0,
          taxTotal: r.taxTotal || 0,
          total: r.total || 0,
          notes: r.notes || '',
          terms: r.terms || '',
          paymentTerms: r.paymentTerms || '',
          paymentType: r.paymentType || (r as any).template_data?.paymentType || (r as any).paymentSchedule?.paymentType,
          template_data: (r as any).template_data,
          paymentSchedule: r.paymentSchedule || (r.milestones?.length ? { milestones: r.milestones } : null),
          items: (r.items || r.lines || []).map(mapIRLine),
          milestones: r.milestones || r.paymentSchedule?.milestones || (r as any).template_data?.milestones || [],
        }));

        // The live invoice-requests hold all revisions and the latest milestone schedules
        const list: InvoiceRequest[] = [...normalizedInvReqs] as any;

        let resolvedTarget: any = null;
        if (initialDirectDoc) {
          const directMs = (Array.isArray(initialDirectDoc.milestones) && initialDirectDoc.milestones.length > 0)
            ? initialDirectDoc.milestones
            : (Array.isArray(initialDirectDoc.paymentSchedule?.milestones) && initialDirectDoc.paymentSchedule.milestones.length > 0)
              ? initialDirectDoc.paymentSchedule.milestones
              : (Array.isArray(initialDirectDoc.template_data?.milestones) && initialDirectDoc.template_data.milestones.length > 0)
                ? initialDirectDoc.template_data.milestones
                : (scheduleData?.milestones || []);
          const directPT = initialDirectDoc.paymentType || initialDirectDoc.template_data?.paymentType || initialDirectDoc.paymentSchedule?.paymentType || (directMs.length > 0 ? 'milestone_based' : 'full_payment');
          const normDirect = {
            ...(initialDirectDoc as any),
            id: initialDirectDoc._id || initialDirectDoc.id,
            _id: initialDirectDoc._id || initialDirectDoc.id,
            number: initialDirectDoc.number || initialDirectDoc.documentNumber || '',
            requestType: 'quotation',
            status: initialDirectDoc.status || 'approved',
            clientName: initialDirectDoc.clientName || initialDirectDoc.customerName || initialDirectDoc.template_data?.clientName || 'Client Company',
            clientEmail: initialDirectDoc.clientEmail || initialDirectDoc.customerEmail || initialDirectDoc.template_data?.clientEmail || '',
            clientPhone: initialDirectDoc.clientPhone || initialDirectDoc.customerPhone || initialDirectDoc.template_data?.clientPhone || '',
            clientAddress: initialDirectDoc.clientAddress || initialDirectDoc.customerAddress || initialDirectDoc.template_data?.clientAddr || '',
            clientGstin: initialDirectDoc.clientGstin || initialDirectDoc.customerGstin || initialDirectDoc.template_data?.clientGstin || '',
            clientState: initialDirectDoc.clientState || initialDirectDoc.customerState || initialDirectDoc.template_data?.clientState || '',
            contactPerson: initialDirectDoc.contactPerson || initialDirectDoc.template_data?.contactPerson || '',
            contactDesignation: initialDirectDoc.contactDesignation || initialDirectDoc.template_data?.contactDesignation || '',
            poNumber: initialDirectDoc.poNumber || initialDirectDoc.referenceNo || '',
            subject: initialDirectDoc.subject || initialDirectDoc.template_data?.subject || '',
            introText: initialDirectDoc.introText || initialDirectDoc.template_data?.introText || '',
            subtotal: initialDirectDoc.subtotal || initialDirectDoc.subTotal || 0,
            taxTotal: initialDirectDoc.taxTotal || 0,
            total: initialDirectDoc.total || initialDirectDoc.grandTotal || 0,
            notes: initialDirectDoc.notes || '',
            terms: initialDirectDoc.terms || '',
            paymentTerms: initialDirectDoc.paymentTerms || '',
            paymentType: directPT,
            template_data: initialDirectDoc.template_data,
            paymentSchedule: initialDirectDoc.paymentSchedule || (directMs.length ? { milestones: directMs } : null),
            items: (initialDirectDoc.items || initialDirectDoc.lines || []).map(mapIRLine),
            milestones: directMs,
          };
          const existIdx = list.findIndex(q => q.id === normDirect.id || (q.number && normDirect.number && q.number === normDirect.number));
          if (existIdx >= 0) {
            list[existIdx] = normDirect as any;
          } else {
            list.unshift(normDirect as any);
          }
          resolvedTarget = normDirect;
        }

        setQuotations(list);

        const target = resolvedTarget || list.find(q => 
          (initialQuotationId && (q.id === initialQuotationId || (q as any)._id === initialQuotationId)) ||
          (initialQuotationNumber && (q.number === initialQuotationNumber || (q as any).quotationNumber === initialQuotationNumber))
        );
        if (target) {
          handleQuotationSelect(target.id || target._id, list, scheduleData || target.paymentSchedule);
        }
      } catch {
        toast.error("Could not load quotations.");
      } finally {
        setQuotsLoading(false);
      }
    })();
  }, [initialQuotationId, initialQuotationNumber, initialScheduleId]);

  const setLine = (key: string, patch: Partial<LineItem>) =>
    setLines(ls => ls.map(l => (l.key === key ? { ...l, ...patch } : l)));

  const pickService = (key: string, val: string) => {
    const s = allMasterServices.find(x => x.name.toLowerCase() === val.toLowerCase());
    if (s) {
      // The service's own tax slab from master data (its HSN/SAC code's slab, then 18%, as the fallback).
      const sf = serviceLineFields(s.name, allMasterServices, hsnSacCodes);
      setLine(key, {
        name: s.name,
        description: s.name,
        hsnSac: s.hsnSac || '998314',
        unit: (s as any)?.defaultUnit || s?.unit || 'Nos',
        rate: s.defaultRate || 0,
        taxRate: sf.gstRate,
        taxMasterId: sf.taxMasterId,
      });
    } else {
      setLine(key, { name: val, description: val });
    }
  };

  const setMs = (key: string, patch: Partial<Milestone>) =>
    setMilestones(ms => ms.map(m => (m.key === key ? { ...m, ...patch } : m)));

  const subtotal = lines.reduce((s, l) => s + lineNet(l), 0);
  const taxTotal = gstEnabled ? lines.reduce((s, l) => s + lineTax(l, true), 0) : 0;
  const grandTotal = subtotal + taxTotal;
  // The tax follows the place of supply: the customer's GSTIN state, else the State chosen here.
  const placeResolved = resolvePlaceOfSupply({ customerGstin: clientGstin, customerState: placeOfSupply || clientState });
  const placeLabel = placeResolved.label || placeOfSupply || clientState || '';
  const proformaInterState = resolveGstMode({
    gstEnabled,
    reverseCharge: false,
    companyGstin: businessGstin,
    customerGstin: clientGstin,
    customerState: placeOfSupply || clientState,
  }) === 'inter';
  const msPct = milestones.reduce((s, m) => s + Number(m.percent || 0), 0);

  // Base Rate guard — every line is checked independently against its master-data
  // service's minimum selling rate after discount (same shared rule as the quotation).
  const baseRateViolations = useMemo(() => findBaseRateViolations(lines, allMasterServices), [lines, allMasterServices]);
  const violationsByLineKey = useMemo(() => {
    const map = new Map<string, BaseRateViolation<LineItem>>();
    for (const v of baseRateViolations) map.set(v.line.key, v);
    return map;
  }, [baseRateViolations]);

  // Post-header-discount payable amounts — used everywhere amounts are shown or saved.
  // subtotal/taxTotal/grandTotal are PRE-discount (line items only); the discount from
  // the quotation is applied here once so all downstream consumers see the correct value.
  const _quotForDisc = selectedQuotDoc || quotations.find(q => q.id === selectedQuotId || (q as any)._id === selectedQuotId) || null;
  const _headerDiscVal  = Number((_quotForDisc as any)?.headerDiscount ?? (_quotForDisc as any)?.template_data?.headerDiscount ?? 0);
  const _headerDiscType = String((_quotForDisc as any)?.headerDiscountType ?? (_quotForDisc as any)?.template_data?.headerDiscountType ?? 'percent');
  const _hDiscAmt = _headerDiscVal > 0 && subtotal > 0
    ? (_headerDiscType === 'percent'
        ? Math.round(subtotal * (_headerDiscVal / 100) * 100) / 100
        : Math.min(_headerDiscVal, subtotal))
    : 0;
  const discountedSubtotal   = Math.max(0, subtotal - _hDiscAmt);
  const _taxScale            = subtotal > 0 && discountedSubtotal > 0 ? discountedSubtotal / subtotal : 1;
  const discountedTaxTotal   = Math.round(taxTotal * _taxScale * 100) / 100;
  // Grand total includes additional charges (payable on top of tax) — same semantics as the
  // quotation form and the rendered template, so stored totals match the document HTML.
  const discountedGrandTotal = Math.round((discountedSubtotal + discountedTaxTotal + (additionalCharges > 0 ? additionalCharges : 0)) * 100) / 100;

  const fmtMoney = (v: number) =>
    "₹" + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });


  const buildPayload = (status: string, mode?: 'draft' | 'submit') => {
    const validLines = lines.filter(l => l.name.trim() && l.quantity > 0);
    const hasMilestones = milestones.length > 0;
    const targetQ = selectedQuotDoc || quotations.find(q => q.id === selectedQuotId || (q as any)._id === selectedQuotId);
    const srcQuotationNo = targetQ?.number || (targetQ as any)?.quotationNumber || initialQuotationNumber || undefined;
    const srcQuotationId = selectedQuotId || targetQ?._id || targetQ?.id || initialQuotationId || undefined;

    const totalGstRate = lines[0]?.taxRate || 18;
    const baseCgstRate = totalGstRate / 2;
    const baseSgstRate = totalGstRate / 2;

    const isMilestone = paymentType === 'milestone_based';
    // headerDiscountVal/Type from targetQ (only available here in buildPayload)
    const headerDiscountVal  = Number((targetQ as any)?.headerDiscount ?? (targetQ as any)?.template_data?.headerDiscount ?? 0);
    const headerDiscountType = String((targetQ as any)?.headerDiscountType ?? (targetQ as any)?.template_data?.headerDiscountType ?? 'percent');
    // discountedSubtotal/discountedTaxTotal/discountedGrandTotal come from component scope

    const formattedMilestones = (isMilestone && milestones.length > 0) ? milestones.map((m, idx) => {
      const pct = Number(m.percent) || 0;
      const netAmt  = Math.round((discountedSubtotal * (pct / 100)) * 100) / 100;
      const cgstAmt = gstEnabled && !proformaInterState ? Math.round(((discountedTaxTotal / 2) * (pct / 100)) * 100) / 100 : 0;
      const sgstAmt = gstEnabled && !proformaInterState ? Math.round(((discountedTaxTotal - (discountedTaxTotal / 2)) * (pct / 100)) * 100) / 100 : 0;
      const igstAmt = gstEnabled && proformaInterState ? Math.round((discountedTaxTotal * (pct / 100)) * 100) / 100 : 0;
      const taxAmt  = cgstAmt + sgstAmt + igstAmt;
      const amt     = Math.round((discountedGrandTotal * (pct / 100)) * 100) / 100;

      const cleanDueDate = (d?: string) => {
        if (!d) return undefined;
        const s = String(d).trim();
        if (!s || s.toLowerCase().includes('mm') || s.toLowerCase().includes('dd') || s.toLowerCase().includes('yyyy')) return undefined;
        return s;
      };

      return {
        milestoneNumber: idx + 1,
        name: m.label || `Stage ${idx + 1}`,
        label: m.label || `Stage ${idx + 1}`,
        percent: pct,
        percentage: pct,
        triggerType: safeTriggerType(m.triggerType, idx, milestones.length),
        description: m.description || '',
        daysFromApproval: Number(m.daysFromApproval || 0),
        dueDate: cleanDueDate(m.dueDate),
        due: cleanDueDate(m.dueDate),
        netAmount: netAmt,
        taxAmount: taxAmt,
        cgstAmount: cgstAmt,
        sgstAmount: sgstAmt,
        igstAmount: igstAmt,
        amount: amt,
        amountDue: amt,
        cgstRate: baseCgstRate,
        sgstRate: baseSgstRate,
        gstRate: totalGstRate,
        milestoneTaxableAmount: netAmt,
        milestoneTaxableAmountMinor: Math.round(netAmt * 100),
        milestoneCGST: cgstAmt,
        milestoneCGSTMinor: Math.round(cgstAmt * 100),
        milestoneSGST: sgstAmt,
        milestoneSGSTMinor: Math.round(sgstAmt * 100),
        milestoneIGST: igstAmt,
        milestoneIGSTMinor: Math.round(igstAmt * 100),
        milestoneGST: taxAmt,
        milestoneGSTMinor: Math.round(taxAmt * 100),
        milestoneTotal: amt,
        milestoneTotalMinor: Math.round(amt * 100),
        amountDueMinor: Math.round(amt * 100),
        amountOutstanding: amt,
        amountOutstandingMinor: Math.round(amt * 100),
        status: (schedDoc?.milestones?.[idx]?.status) || 'pending',
        invoiceNumber: (schedDoc?.milestones?.[idx]?.invoiceNumber) || '',
        proformaNumber: computedProformaNo || (targetQ as any)?.proformaNumber || '',
      };
    }) : [];

    // `computedProformaNo` comes from component scope (useMemo) — it must not be
    // re-declared here: formattedMilestones above already references it while building.

    const isLock = isConfirmed;

    const generatedHtml = generateFullProformaInvoiceHtml({
      customerName: clientName.trim(),
      customerEmail: clientEmail.trim(),
      customerPhone: clientPhone.trim(),
      customerAddress: clientAddress.trim(),
      clientGstin: clientGstin.trim(),
      clientState: placeLabel,
      contactPerson: contactPerson.trim(),
      proformaNumber: computedProformaNo,
      quotationNumber: srcQuotationNo || (targetQ as any)?.number || (editingDraft as any)?.sourceQuotationNumber || (editingDraft as any)?.number,
      quotationDate: issueDate,
      validUntil: validUntil || undefined,
      paymentTerms: paymentTerms,
      referenceNo: poNumber.trim(),
      notes: notes,
      terms: (termsList && termsList.filter(s => s && s.trim()).length > 0) ? termsList.filter(s => s && s.trim()).join('\n') : terms,
      termsList: (termsList && termsList.filter(s => s && s.trim()).length > 0) ? termsList.filter(s => s && s.trim()) : (terms ? terms.split('\n').filter(Boolean) : undefined),
      assumptions: assumptionsSectionEnabled ? ((assumptionsList && assumptionsList.filter(s => s && s.trim()).length > 0) ? assumptionsList.filter(s => s && s.trim()).join('\n') : assumptions) : '',
      assumptionsList: assumptionsSectionEnabled ? ((assumptionsList && assumptionsList.filter(s => s && s.trim()).length > 0) ? assumptionsList.filter(s => s && s.trim()) : (assumptions ? assumptions.split('\n').filter(Boolean) : undefined)) : undefined,
      assumptionsEnabled: assumptionsSectionEnabled,
      declaration: declaration,
      companyName: businessName || '',
      companyAddress: businessAddress || '',
      companyTagline: businessTagline || '',
      companyLogo: companyProfile?.logoUrl || '',
      companyGstin: businessGstin || '',
      companyPan: businessPan || '',
      companyState: (companyProfile as any)?.registeredAddress?.state || '',
      companyPhone: businessPhone || '',
      companyEmail: businessEmail || '',
      companyWeb: businessWeb || '',
      signatureUrl: companyProfile?.signatureUrl || '',
      bankAccName: bankAccName,
      bankAccNo: bankAccNo,
      bankName: bankName,
      bankBranch: bankBranch,
      bankIfsc: bankIfsc,
      bankUpi: bankUpi,
      preparedBy: preparedBy || undefined,
      currency: currency || 'INR',
      gstEnabled: gstEnabled,
      reverseCharge: reverseCharge ? 'Yes' : 'No',
      placeOfSupply: placeLabel,
      items: validLines.map(l => ({
        name: l.name.trim(),
        description: l.name.trim(),
        // The template renders subDescription verbatim, so the per-line note rides inside it.
        subDescription: (l.notes || '').trim() || undefined,
        notes: (l.notes || '').trim(),
        hsnSac: l.hsnSac,
        hsn: l.hsnSac,
        unit: l.unit || 'Nos',
        quantity: l.quantity,
        qty: l.quantity,
        rate: l.rate,
        unitPrice: l.rate,
        discountPercent: l.discountPercent,
        discount: l.discountPercent,
        disc: l.discountPercent,
        taxRate: l.taxRate,
        taxMasterId: l.taxMasterId || null,
        gstRate: l.taxRate,
        gst: l.taxRate,
        cgstRate: l.cgstRate,
        sgstRate: l.sgstRate,
        igstRate: l.igstRate,
        catalogItemId: l.catalogItemId || undefined,
        taxableValue: lineNet(l),
        taxAmount: gstEnabled ? lineTax(l, true) : 0,
        lineTotal: lineNet(l) + (gstEnabled ? lineTax(l, true) : 0),
        total: lineNet(l) + (gstEnabled ? lineTax(l, true) : 0),
      })),
      subtotal: subtotal,
      taxTotal: taxTotal,
      grandTotal: grandTotal,
      stagePercent: undefined,
      milestoneIndex: undefined,
      stageAmountsAreFinal: false,
      headerDiscount: headerDiscountVal,
      headerDiscountType: headerDiscountType as 'fixed' | 'percent' | undefined,
      additionalCharges: additionalCharges > 0 ? additionalCharges : undefined,
      additionalChargesLabel: additionalChargesLabel || undefined,

      discount: (targetQ as any)?.discount ?? (targetQ as any)?.template_data?.discount,
      // Use post-discount values so the milestone schedule table splits the true payable amount.
      contractTaxable: discountedSubtotal,
      contractGrand:   discountedGrandTotal,
      contractTax:     discountedTaxTotal,
      paymentType: isMilestone ? 'milestone_based' : 'full_payment',
      milestones: isMilestone ? formattedMilestones : [],
      docType: 'proforma',
      isConfirmed: isLock,
      isLocked: isLock,
      displayOptions: {
        showHsnColumn: showHsnColumn,
        showTaxSummary: showTaxSummary,
        showSkuColumn: showSkuColumn,
        showSerialNumbers: showSerialNumbers,
        showThumbnails: showThumbnails,
        showDescFullWidth: showDescFullWidth,
        hideSubtotalForGroups: hideSubtotalForGroups,
        hidePlaceOfSupply: hidePlaceOfSupply,
        displayUnit: displayUnit,
      },
    });

    const lockedOrRawHtml = isLock ? lockHtmlTemplate(generatedHtml) : generatedHtml;

    const payload = {
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim(),
      clientPhone: clientPhone.trim(),
      clientAddress: clientAddress.trim(),
      clientGstin: clientGstin.trim(),
      clientState: placeLabel,
      contactPerson: contactPerson.trim(),
      contactDesignation: contactDesignation.trim(),
      poNumber: poNumber.trim(),
      terms: (termsList && termsList.filter(s => s && s.trim()).length > 0) ? termsList.filter(s => s && s.trim()).join('\n') : terms,
      termsList: (termsList && termsList.filter(s => s && s.trim()).length > 0) ? termsList.filter(s => s && s.trim()) : [],
      assumptions: assumptionsSectionEnabled ? ((assumptionsList && assumptionsList.filter(s => s && s.trim()).length > 0) ? assumptionsList.filter(s => s && s.trim()).join('\n') : assumptions) : '',
      assumptionsList: assumptionsSectionEnabled ? ((assumptionsList && assumptionsList.filter(s => s && s.trim()).length > 0) ? assumptionsList.filter(s => s && s.trim()) : []) : [],
      declaration: declaration,
      declarationId: declarationId,
      businessName: businessName,
      businessAddress: businessAddress,
      businessGstin: businessGstin,
      businessPan: businessPan,
      businessPhone: businessPhone,
      businessEmail: businessEmail,
      businessWeb: businessWeb,
      gstEnabled: gstEnabled,
      reverseCharge: reverseCharge,
      bankDetailId: bankDetailId,
      bankAccName: bankAccName,
      bankAccNo: bankAccNo,
      bankName: bankName,
      bankBranch: bankBranch,
      bankIfsc: bankIfsc,
      bankUpi: bankUpi,
      preparedBy: preparedBy.trim(),
      currency: currency,
      clientId: clientId,
      additionalCharges: additionalCharges,
      additionalChargesLabel: additionalChargesLabel,
      shippingEnabled: shippingEnabled,
      shippingName: shippingName.trim(),
      shippingAddress: shippingAddress.trim(),
      shippingCity: shippingCity.trim(),
      shippingState: shippingState.trim(),
      shippingPincode: shippingPincode.trim(),
      displayOptions: {
        showHsnColumn: showHsnColumn,
        showTaxSummary: showTaxSummary,
        showSkuColumn: showSkuColumn,
        showSerialNumbers: showSerialNumbers,
        showThumbnails: showThumbnails,
        showDescFullWidth: showDescFullWidth,
        hideSubtotalForGroups: hideSubtotalForGroups,
        hidePlaceOfSupply: hidePlaceOfSupply,
        displayUnit: displayUnit,
      },
      sourceQuotationId: srcQuotationId,
      sourceQuotationNumber: srcQuotationNo,
      requestType: 'proforma',
      status,
      isConfirmed: isLock,
      number: computedProformaNo || undefined,
      proformaNumber: computedProformaNo || undefined,
      headerDiscount: (targetQ as any)?.headerDiscount ?? (targetQ as any)?.template_data?.headerDiscount ?? 0,
      headerDiscountType: (targetQ as any)?.headerDiscountType ?? (targetQ as any)?.template_data?.headerDiscountType ?? 'percent',
      items: validLines.map(l => ({
        name: l.name.trim(),
        description: l.description.trim() || l.name.trim(),
        notes: (l.notes || '').trim(),
        hsnSac: l.hsnSac,
        hsn: l.hsnSac,
        unit: l.unit || 'Nos',
        quantity: l.quantity,
        qty: l.quantity,
        rate: l.rate,
        unitPrice: l.rate,
        discountPercent: l.discountPercent,
        discount: l.discountPercent,
        disc: l.discountPercent,
        taxRate: l.taxRate,
        taxMasterId: l.taxMasterId || null,
        gstRate: l.taxRate,
        cgstRate: l.cgstRate,
        sgstRate: l.sgstRate,
        igstRate: l.igstRate,
        catalogItemId: l.catalogItemId || undefined,
        taxableValue: lineNet(l),
        taxAmount: gstEnabled ? lineTax(l, true) : 0,
        lineTotal: lineNet(l) + (gstEnabled ? lineTax(l, true) : 0),
        total: lineNet(l) + (gstEnabled ? lineTax(l, true) : 0),
      })),
      notes,
      date: issueDate,
      validUntil: validUntil || undefined,
      paymentTerms,
      // Store post-discount amounts so the DB record reflects the actual payable amount.
      subtotal: discountedSubtotal,
      taxTotal: discountedTaxTotal,
      total: discountedGrandTotal,
      subtotalMinor: Math.round(discountedSubtotal * 100),
      taxTotalMinor: Math.round(discountedTaxTotal * 100),
      totalMinor: Math.round(discountedGrandTotal * 100),
      grandTotalMinor: Math.round(discountedGrandTotal * 100),
      amountDue: discountedGrandTotal,
      amountDueMinor: Math.round(discountedGrandTotal * 100),
      paymentType: isMilestone ? 'milestone_based' : 'full_payment',
      paymentSchedule: isMilestone ? {
        paymentType: 'milestone_based',
        totalAmount: discountedGrandTotal,
        subtotal: discountedSubtotal,
        taxTotal: discountedTaxTotal,
        milestones: formattedMilestones,
      } : {
        paymentType: 'full_payment',
        totalAmount: discountedGrandTotal,
        subtotal: discountedSubtotal,
        taxTotal: discountedTaxTotal,
        milestones: [],
      },
      milestones: isMilestone ? formattedMilestones : [],
      template_data: {
        ...((targetQ as any)?.template_data || {}),
        companyLogo: companyProfile?.logoUrl || '',
        signatureUrl: companyProfile?.signatureUrl || '',
        bankAccName: bankAccName,
        bankAccNo: bankAccNo,
        bankName: bankName,
        bankBranch: bankBranch,
        bankIfsc: bankIfsc,
        bankUpi: bankUpi,
      items: validLines.map(l => ({
        name: l.name.trim(),
        description: l.description.trim() || l.name.trim(),
        notes: (l.notes || '').trim(),
        hsnSac: l.hsnSac,
        hsn: l.hsnSac,
        unit: l.unit || 'Nos',
        quantity: l.quantity,
        qty: l.quantity,
        rate: l.rate,
        unitPrice: l.rate,
        discountPercent: l.discountPercent,
        discount: l.discountPercent,
        disc: l.discountPercent,
        taxRate: l.taxRate,
        taxMasterId: l.taxMasterId || null,
        gstRate: l.taxRate,
        gst: l.taxRate,
        cgstRate: l.cgstRate,
        sgstRate: l.sgstRate,
        igstRate: l.igstRate,
        catalogItemId: l.catalogItemId || undefined,
        taxableValue: lineNet(l),
        taxAmount: gstEnabled ? lineTax(l, true) : 0,
        lineTotal: lineNet(l) + (gstEnabled ? lineTax(l, true) : 0),
        total: lineNet(l) + (gstEnabled ? lineTax(l, true) : 0),
        })),
        milestones: isMilestone ? formattedMilestones : [],
        paymentType: isMilestone ? 'milestone_based' : 'full_payment',
        subtotal: discountedSubtotal,
        taxTotal: discountedTaxTotal,
        total: discountedGrandTotal,
        grandTotal: discountedGrandTotal,

        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        clientPhone: clientPhone.trim(),
        clientAddress: clientAddress.trim(),
        clientGstin: clientGstin.trim(),
        clientState: placeLabel,
        contactPerson: contactPerson.trim(),
        contactDesignation: contactDesignation.trim(),
        poNumber: poNumber.trim(),
        referenceNo: poNumber.trim(),
        notes: notes,
        terms: (termsList && termsList.filter(s => s && s.trim()).length > 0) ? termsList.filter(s => s && s.trim()).join('\n') : terms,
        termsList: (termsList && termsList.filter(s => s && s.trim()).length > 0) ? termsList.filter(s => s && s.trim()) : [],
        assumptions: assumptionsSectionEnabled ? ((assumptionsList && assumptionsList.filter(s => s && s.trim()).length > 0) ? assumptionsList.filter(s => s && s.trim()).join('\n') : assumptions) : '',
        assumptionsList: assumptionsSectionEnabled ? ((assumptionsList && assumptionsList.filter(s => s && s.trim()).length > 0) ? assumptionsList.filter(s => s && s.trim()) : []) : [],
        declaration: declaration,
        declarationId: declarationId,
        bankDetailId: bankDetailId,
        preparedBy: preparedBy.trim(),
        currency: currency,
        clientId: clientId,
        additionalCharges: additionalCharges,
        additionalChargesLabel: additionalChargesLabel,
        headerDiscount: headerDiscount,
        headerDiscountType: headerDiscountType,
        shippingEnabled: shippingEnabled,
        shippingName: shippingName.trim(),
        shippingAddress: shippingAddress.trim(),
        shippingCity: shippingCity.trim(),
        shippingState: shippingState.trim(),
        shippingPincode: shippingPincode.trim(),
        displayOptions: {
          showHsnColumn: showHsnColumn,
          showTaxSummary: showTaxSummary,
          showSkuColumn: showSkuColumn,
          showSerialNumbers: showSerialNumbers,
          showThumbnails: showThumbnails,
          showDescFullWidth: showDescFullWidth,
          hideSubtotalForGroups: hideSubtotalForGroups,
          hidePlaceOfSupply: hidePlaceOfSupply,
          displayUnit: displayUnit,
        },
        businessTag: businessTagline,
      },
      milestoneIndex: null,
      milestonePhaseLabel: '',
      currentHtmlBody: lockedOrRawHtml,
      proformaHtmlBody: lockedOrRawHtml,
    };

    return payload;
  };

  // Company and bank details come from master data and are read-only here; a document can't be saved without them.
  const masterDataIssues = {
    company: missingCompanyDetails({ name: businessName, address: businessAddress, gstin: businessGstin }),
    bank: missingBankDetails({ accountName: bankAccName, accountNumber: bankAccNo, bankName, ifsc: bankIfsc }),
  };
  const masterDataBlocked = hasMasterDataIssues(masterDataIssues);

  const handleSave = async (mode: 'draft' | 'submit') => {
    if (masterDataBlocked) return toast.error(describeMasterDataIssues(masterDataIssues));
    if (!clientName.trim()) return toast.error('Client name is required.');
    const validLines = lines.filter(l => l.name.trim() && l.quantity > 0);
    if (validLines.length === 0) return toast.error('Add at least one line item.');
    if (mode === 'submit') {
      const belowBaseRate = findBaseRateViolations(validLines, allMasterServices);
      if (belowBaseRate.length > 0) {
        const names = Array.from(new Set(belowBaseRate.map(v => v.name))).join(', ');
        return toast.error(`Proforma cannot be submitted because one or more services are below their minimum Base Rate: ${names}.`);
      }
    }
    if (mode === 'submit') {
      const noTaxType = validLines.filter(l => !l.taxMasterId);
      if (noTaxType.length > 0) {
        return toast.error(`Pick the tax type for every line before submitting: ${noTaxType.map(l => l.name).join(', ')}.`);
      }
    }
    if (mode === 'submit') {
      const placeIssue = placeOfSupplyProblem({ gstEnabled, customerGstin: clientGstin, customerState: placeOfSupply || clientState });
      if (placeIssue) return toast.error(placeIssue);
    }
    if (paymentType === 'milestone_based') {
      if (milestones.length === 0) {
        return toast.error('Please add at least one milestone stage.');
      }
      if (msPct !== 100) {
        return toast.error(`Milestone percentages must total 100% (currently ${msPct}%).`);
      }
    }

    const targetDocId = documentId || editId;
    const isRevisionOrResubmit = (isResubmission || isRevision) && Boolean(targetDocId);

    // A proforma needs the superadmin's approval. Submitting moves a draft or rejected proforma
    // forward; for the superadmin that is approval at once, and a pending one is approved here.
    const docStatus = String((editingDraft as any)?.status || '');
    const postAction = (id: string, action: string) => api.post(`/api/invoice-requests/${id}/${action}`, {}, { skipErrorToast: true });
    const submitForApproval = async (id: string) => {
      if (['draft', 'proforma_draft'].includes(docStatus)) await postAction(id, 'submit');
      else if (docStatus === 'proforma_rejected') await postAction(id, 'resubmit-proforma');
    };
    const ensureApproved = async (id: string) => {
      if (['proforma_requested', 'proforma_under_review'].includes(docStatus)) await postAction(id, 'approve-proforma');
      else await submitForApproval(id);
    };

    // A proforma is only ever created from a quotation.
    const sourceQuotation = selectedQuotId || selectedQuotDoc?.id || selectedQuotDoc?._id || initialQuotationId || (editingDraft as any)?.sourceQuotationId;
    if (!targetDocId && !sourceQuotation) return toast.error('Choose the quotation this proforma is for.');

    setSaving(true);
    try {
      if (isRevisionOrResubmit && targetDocId) {
        // REVISION / RESUBMISSION MODE: update document with new data and generated HTML, then call the resubmit endpoint
        const targetStatus = 'proforma_requested';
        const payload = {
          ...buildPayload(targetStatus, mode),
          clientEmail: clientEmail.trim(),
          clientPhone: clientPhone.trim(),
          clientAddress: clientAddress.trim(),
          clientGstin: clientGstin.trim(),
          clientState: placeLabel,
          date: issueDate,
          validUntil: validUntil || undefined,
          paymentTerms,
          subtotal: discountedSubtotal,
          taxTotal: discountedTaxTotal,
          total: discountedGrandTotal,
          subtotalMinor: Math.round(discountedSubtotal * 100),
          taxTotalMinor: Math.round(discountedTaxTotal * 100),
          totalMinor: Math.round(discountedGrandTotal * 100),
          grandTotalMinor: Math.round(discountedGrandTotal * 100),
          amountDue: discountedGrandTotal,
          amountDueMinor: Math.round(discountedGrandTotal * 100),
          isConfirmed,
          status: targetStatus,
          milestones: milestones.map((m, idx) => ({
            milestoneNumber: idx + 1,
            label: m.label || `Stage ${idx + 1}`,
            name: m.label || `Stage ${idx + 1}`,
            percent: Number(m.percent || 0),
            percentage: Number(m.percent || 0),
            triggerType: safeTriggerType(m.triggerType, idx, milestones.length),
            description: m.description || '',
            daysFromApproval: Number(m.daysFromApproval || 0),
            dueDate: m.dueDate && !m.dueDate.includes('mm') && !m.dueDate.includes('dd') && !m.dueDate.includes('yyyy') ? m.dueDate : undefined,
            amount: Math.round(discountedGrandTotal * (Number(m.percent || 0) / 100) * 100) / 100,
            amountDue: Math.round(discountedGrandTotal * (Number(m.percent || 0) / 100) * 100) / 100,
          })),
        };
        await api.put(`/api/invoice-requests/${targetDocId}`, payload);

        // Resubmit the revised Proforma and create its revision snapshot.
        await api.post(`/api/invoice-requests/${targetDocId}/resubmit-proforma`, {
          note: isRevision ? `Revised as ${nextRevisionLabel || 'next revision'}` : `Resubmitted as ${nextRevisionLabel || 'next revision'}`,
          items: payload.items,
          notes,
        });
        toast.success(`Proforma ${isRevision ? 'revised' : 'resubmitted'} as ${nextRevisionLabel || 'next revision'}.`);
      } else if (isEditMode) {
        // EDIT MODE: update the existing draft via PUT
        const targetStatus = mode === 'draft' ? 'draft' : 'proforma_requested';
        const payload = {
          ...buildPayload(targetStatus, mode),
          clientEmail: clientEmail.trim(),
          clientPhone: clientPhone.trim(),
          clientAddress: clientAddress.trim(),
          clientGstin: clientGstin.trim(),
          clientState: placeLabel,
          date: issueDate,
          validUntil: validUntil || undefined,
          paymentTerms,
          isConfirmed,
          milestones: milestones.map((m, idx) => ({
            milestoneNumber: idx + 1,
            label: m.label || `Stage ${idx + 1}`,
            name: m.label || `Stage ${idx + 1}`,
            percent: Number(m.percent || 0),
            percentage: Number(m.percent || 0),
            triggerType: safeTriggerType(m.triggerType, idx, milestones.length),
            description: m.description || '',
            daysFromApproval: Number(m.daysFromApproval || 0),
            dueDate: m.dueDate && !m.dueDate.includes('mm') && !m.dueDate.includes('dd') && !m.dueDate.includes('yyyy') ? m.dueDate : undefined,
            amount: Math.round(discountedGrandTotal * (Number(m.percent || 0) / 100) * 100) / 100,
            amountDue: Math.round(discountedGrandTotal * (Number(m.percent || 0) / 100) * 100) / 100,
          })),
          status: targetStatus,
        };
        await api.put(`/api/invoice-requests/${editId}`, payload, { skipErrorToast: true });

        if (mode === 'submit') {
          await (approvesOwnProforma ? ensureApproved(editId) : submitForApproval(editId));
          toast.success(approvesOwnProforma ? 'Proforma approved.' : 'Proforma submitted for approval.');
        } else {
          toast.success(mode === 'draft' ? 'Proforma draft updated successfully.' : 'Proforma updated successfully.');
        }
      } else {
        // CREATE MODE: new proforma
        const quotId = selectedQuotDoc?.id || selectedQuotDoc?._id || initialQuotationId || selectedQuotId || (editingDraft as any)?.sourceQuotationId;
        const sanitizedMilestones = milestones.map((m, idx) => ({
          milestoneNumber: idx + 1,
          label: m.label || `Stage ${idx + 1}`,
          name: m.label || `Stage ${idx + 1}`,
          percent: Number(m.percent || 0),
          percentage: Number(m.percent || 0),
          triggerType: safeTriggerType(m.triggerType, idx, milestones.length),
          description: m.description || '',
          daysFromApproval: Number(m.daysFromApproval || 0),
          dueDate: m.dueDate && !m.dueDate.includes('mm') && !m.dueDate.includes('dd') && !m.dueDate.includes('yyyy') ? m.dueDate : undefined,
          amount: Math.round(discountedGrandTotal * (Number(m.percent || 0) / 100) * 100) / 100,
          amountDue: Math.round(discountedGrandTotal * (Number(m.percent || 0) / 100) * 100) / 100,
        }));

        if (mode === 'draft') {
          await actions.createProforma.mutateAsync(buildPayload('draft', 'draft'));
          toast.success('Proforma saved as draft.');
        } else {
          const targetStatus = 'proforma_requested';
          await actions.createProforma.mutateAsync(buildPayload(targetStatus, 'submit'));
          toast.success(approvesOwnProforma ? 'Proforma created and approved.' : 'Proforma submitted for approval.');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      queryClient.invalidateQueries({ queryKey: ['milestones'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['accounting-schedules'] });
      onSuccess?.();
      onClose();
      navigate('/app/sales/proforma-invoices');
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'save this proforma'));
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className={asPage ? `${PAGE_BG} min-h-screen py-4 px-2 sm:px-4` : "fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-6 sm:py-8 px-3 sm:px-4"}>
      <div className={asPage ? "w-full max-w-6xl mx-auto rounded-2xl border border-slate-300 dark:border-slate-600 overflow-hidden" : "w-full max-w-5xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl mb-8 border border-slate-300 dark:border-slate-600 overflow-hidden"}>
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl border border-indigo-100 dark:border-indigo-800/50">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                {isRevision
                  ? 'Revise Proforma Invoice'
                  : isResubmitMode
                    ? 'Resubmit Proforma Invoice'
                    : isEditMode
                      ? (isConfirmed ? 'Proforma Invoice (Approved & Locked)' : 'Edit Proforma Invoice')
                      : 'New Proforma Invoice'}
                {isConfirmed && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                    <Lock className="w-3 h-3" /> Approved &amp; Locked
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {isConfirmed
                  ? 'This Proforma Invoice has been confirmed. All fields and editable templates are locked.'
                  : isRevision
                    ? `Revising Proforma: ${proformaNumber || '—'} · Current: ${currentRevisionLabel || 'R1'} → Next: ${nextRevisionLabel || 'R2'}`
                    : isResubmitMode
                      ? `Proforma: ${proformaNumber || '—'} · Current: ${currentRevisionLabel || 'R1'} → Next: ${nextRevisionLabel || 'R2'}`
                      : isEditMode
                        ? 'Update draft details — changes are saved back to the database'
                        : 'Fill in the details below to generate and issue a proforma invoice'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Confirmed & Locked Banner */}
        {isConfirmed && (
          <div className="mx-5 sm:mx-6 mt-4 rounded-xl border border-emerald-300/80 bg-emerald-50/80 dark:bg-emerald-950/40 p-4 text-emerald-900 dark:text-emerald-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/60 rounded-lg text-emerald-700 dark:text-emerald-300 shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-emerald-900 dark:text-emerald-200">
                    Proforma Invoice Approved &amp; Locked
                  </h3>
                  <Badge className="bg-emerald-600 text-white text-[11px] font-bold">Confirmed</Badge>
                </div>
                <p className="text-xs text-emerald-800/90 dark:text-emerald-300/90 mt-1">
                  This proforma invoice has been confirmed. The proforma details and editable template are locked and cannot be edited.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Revision Banner */}
        {isRevision && (
          <div className="mx-5 sm:mx-6 mt-4 rounded-xl border border-indigo-200 bg-indigo-50/80 dark:bg-indigo-950/30 p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg text-indigo-600 dark:text-indigo-400 shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-300">
                  Revision Mode ({nextRevisionLabel || 'R2'})
                </h3>
                <p className="text-xs text-indigo-700 dark:text-indigo-400 mt-1">
                  All previous details, line items, notes, and milestones from quotation/proforma are loaded below. Update any values needed before submitting the revised Proforma.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Previous Rejection Banner */}
        {isResubmitMode && rejectionReason && (
          <div className="mx-5 sm:mx-6 mt-4 rounded-xl border border-rose-200 bg-rose-50/80 dark:bg-rose-950/30 p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-rose-100 dark:bg-rose-900/50 rounded-lg text-rose-600 dark:text-rose-400 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-rose-800 dark:text-rose-300">Previous Rejection Reason</h3>
                <p className="text-xs text-rose-700 dark:text-rose-400 mt-1 whitespace-pre-wrap">{rejectionReason}</p>
              </div>
            </div>
          </div>
        )}

        {/* In page mode the outer canvas is already grey; in modal mode this div is the grey body behind the white cards */}
        <div className={asPage ? "py-4 sm:py-5" : `p-4 sm:p-6 ${PAGE_BG}`}>
          <div aria-disabled={isConfirmed} className={`space-y-4 sm:space-y-5 ${isConfirmed ? 'opacity-70 pointer-events-none' : ''}`}>
            {linkedDeal && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-teal-500/10 border border-emerald-500/25 text-xs text-emerald-900 dark:text-emerald-200 shadow-xs">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-600 text-white shadow-xs shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-900 dark:text-white">
                        Linked CRM Deal
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Proforma Invoice dynamically linked to deal: <span className="font-semibold text-slate-800 dark:text-slate-200">{linkedDeal.client || linkedDeal.clientName || clientName || subject}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="px-2.5 py-1 rounded-md bg-emerald-100 dark:bg-emerald-950/80 border border-emerald-300 dark:border-emerald-800 text-[11px] font-mono font-semibold text-emerald-800 dark:text-emerald-300">
                    {linkedDeal.id || linkedDeal._id ? `Deal ID: ${linkedDeal.id || linkedDeal._id}` : 'Deal Details Auto-Loaded'}
                  </span>
                </div>
              </div>
            )}

            {/* Quick-Fill Sources Card */}
            <div className={cardCls + " border-indigo-100 dark:border-indigo-900/40 bg-gradient-to-br from-indigo-50/40 to-white dark:from-indigo-950/20 dark:to-slate-900"}>
              <div className="flex items-center gap-2 pb-3 mb-3.5 border-b border-indigo-100/70 dark:border-indigo-900/40">
                <div className="p-1.5 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-indigo-900 dark:text-indigo-300 uppercase tracking-wider">
                    Quick-Fill from Existing Records
                  </h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Auto-populate customer, services, milestones and terms from approved quotations or CRM deals
                  </p>
                </div>
              </div>

              <div className="space-y-3.5">
                <div>
                  <label className={labelCls}>
                    Quotation *
                  </label>
                  <div className="relative">
                    <select
                      className={cls + " pr-10 appearance-none font-medium"}
                      value={selectedQuotId}
                      onChange={e => handleQuotationSelect(e.target.value)}
                      disabled={quotsLoading || initialMilestoneIndex !== undefined}
                    >
                      <option value="">
                        {quotsLoading ? "Loading quotations…" : "— Choose the quotation for this proforma —"}
                      </option>
                      {quotations
                        // Only approved quotations can be turned into a proforma.
                        .filter(q => isQuotationUsable(q as any) || q.id === selectedQuotId)
                        .map(q => (
                        <option key={q.id} value={q.id}>
                          {(q.clientName || "Unnamed Client") + " — #" + q.number + " (" + fmtMoney(q.total) + ")"}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                  {quotations.length === 0 && !quotsLoading && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">No quotations found. Create a quotation first; a proforma is always raised from a quotation.</p>
                  )}
                  {selectedQuotId && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5 font-medium flex items-center gap-1">
                      ✓ All details, lines, and terms loaded from quotation. You can modify any values before submitting.
                    </p>
                  )}
                </div>

              </div>
            </div>

            {/* General Invoice Details Card */}
            <div className={cardCls}>
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                  <Calendar className="w-4 h-4" />
                </div>
                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                  Invoice Details &amp; Scope
                </h3>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                  <div>
                    <label className={labelCls}>Issue Date <span className="text-red-500">*</span></label>
                    <input type="date" className={cls} value={issueDate} onChange={e => setIssueDate(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Valid Until</label>
                    <input type="date" className={cls} value={validUntil} onChange={e => setValidUntil(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Payment Terms</label>
                    <select
                      className={cls}
                      value={customPaymentTerm || (!selectedPaymentTerm && paymentTerms) ? PAYMENT_TERM_CUSTOM : (selectedPaymentTerm?._id || '')}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === PAYMENT_TERM_CUSTOM) { setCustomPaymentTerm(true); return; }
                        setCustomPaymentTerm(false);
                        const name = activePaymentTerms.find(t => t._id === v)?.name || '';
                        setPaymentTerms(name);
                        applyPaymentTermToMilestones(name);
                      }}
                    >
                      <option value="">— Select a payment term —</option>
                      {activePaymentTerms.map(t => (
                        <option key={t._id} value={t._id}>{t.name}</option>
                      ))}
                      <option value={PAYMENT_TERM_CUSTOM}>Custom…</option>
                    </select>
                    {(customPaymentTerm || (!selectedPaymentTerm && paymentTerms)) && (
                      <input
                        className={cls + ' mt-2'}
                        value={paymentTerms}
                        onChange={e => {
                          setPaymentTerms(e.target.value);
                          applyPaymentTermToMilestones(e.target.value);
                        }}
                        placeholder="e.g. 100% Full Payment on Delivery / Order, or 30-40-30 for milestone stages"
                      />
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>PO / Reference Number</label>
                    <input className={cls} value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="e.g. PO-2026-081" />
                  </div>
                </div>

              </div>
            </div>

            <MasterDataMissingBanner company={masterDataIssues.company} bank={masterDataIssues.bank} />

            {/* Company + Client side-by-side cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Company Details Card */}
              <div className={cardCls}>
                <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                    Proforma From (Company Details)
                  </h3>
                </div>

                <div className="-mt-2 mb-3"><LockedHint source="company" /></div>
                <div className="space-y-3.5">
                  {/* Logo + Company Name + Tagline */}
                  <div className="flex items-start gap-3.5">
                    <div className="shrink-0 w-16 h-16 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center bg-slate-50 dark:bg-slate-800/60 overflow-hidden">
                      {companyProfile?.logoUrl ? (
                        <img src={companyProfile.logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                      ) : (
                        <span className="text-[10px] text-slate-400 text-center leading-tight">Company<br/>Logo</span>
                      )}
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <label className={labelCls}>Company / Brand Name <span className="text-red-500">*</span></label>
                        <input className={cls + " " + LOCKED_INPUT_CLASS} value={businessName} readOnly aria-readonly="true" />
                      </div>
                      <div>
                        <label className={labelCls}>Tagline</label>
                        <input className={cls + " " + LOCKED_INPUT_CLASS} value={businessTagline} readOnly aria-readonly="true" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Registered Office Address</label>
                    <textarea rows={2} className={cls + " " + LOCKED_INPUT_CLASS + " resize-none"} value={businessAddress} readOnly aria-readonly="true" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Company GSTIN</label>
                      <input className={cls + " " + LOCKED_INPUT_CLASS} value={businessGstin} readOnly aria-readonly="true" />
                    </div>
                    <div>
                      <label className={labelCls}>Company PAN</label>
                      <input className={cls + " " + LOCKED_INPUT_CLASS} value={businessPan} readOnly aria-readonly="true" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>Phone</label>
                      <input className={cls + " " + LOCKED_INPUT_CLASS} value={businessPhone} readOnly aria-readonly="true" />
                    </div>
                    <div>
                      <label className={labelCls}>Email</label>
                      <input type="email" className={cls + " " + LOCKED_INPUT_CLASS} value={businessEmail} readOnly aria-readonly="true" />
                    </div>
                    <div>
                      <label className={labelCls}>Website</label>
                      <input className={cls + " " + LOCKED_INPUT_CLASS} value={businessWeb} readOnly aria-readonly="true" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Client Details Card */}
              <div className={cardCls}>
                <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                    <User className="w-4 h-4" />
                  </div>
                  <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                    Client Details (Bill To)
                  </h3>
                </div>

                <div className="space-y-3.5">
                  <div>
                    <label className={labelCls}>Client / Company Name <span className="text-red-500">*</span></label>
                    <input className={cls} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client / Business name" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Contact Person</label>
                      <input className={cls} value={contactPerson} onChange={e => setContactPerson(e.target.value)} placeholder="Contact Person Name" />
                    </div>
                    <div>
                      <label className={labelCls}>Designation</label>
                      <input className={cls} value={contactDesignation} onChange={e => setContactDesignation(e.target.value)} placeholder="e.g. CTO, Procurement" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Client Email</label>
                      <input type="email" className={cls} value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="client@email.com" />
                    </div>
                    <div>
                      <label className={labelCls}>Client Phone</label>
                      <input className={cls} value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="+91 XXXXX XXXXX" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Client GSTIN</label>
                    <input className={cls} value={clientGstin} onChange={e => setClientGstin(e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" />
                  </div>
                  <div>
                    <label className={labelCls}>Client Billing Address</label>
                    <textarea rows={2} className={cls} value={clientAddress} onChange={e => setClientAddress(e.target.value)} placeholder="Client billing address" />
                  </div>
                </div>
              </div>
            </div>

            {/* GST Configuration Card */}
            <div className={cardCls}>
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                  <Receipt className="w-4 h-4" />
                </div>
                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                  GST &amp; Tax Configuration
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={gstEnabled}
                    onChange={e => setGstEnabled(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500/40"
                  />
                  <div>
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block">Enable GST</span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 block">Calculate and display tax on invoice</span>
                  </div>
                </label>

                <PlaceOfSupplyField
                  customerGstin={clientGstin}
                  state={placeOfSupply || clientState}
                  sellerGstin={businessGstin}
                  gstApplies={gstEnabled}
                  onChange={(state) => { setPlaceOfSupply(state); setClientState(state); }}
                  inputClassName={cls}
                  labelClassName={labelCls}
                />

                <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={reverseCharge}
                    onChange={e => setReverseCharge(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500/40"
                  />
                  <div>
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block">Reverse Charge</span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 block">Recipient is liable to pay tax</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Line Items Card */}
            <div className={cardCls}>
              <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                    <Layers className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Line Items &amp; Pricing
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Services, rates, HSN codes, discounts and GST rates
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setLines(ls => [...ls, emptyLine()])}
                  className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-200 dark:border-indigo-800/60 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Line Item
                </button>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xs">
                <table className="w-full text-xs min-w-[1200px]">
                  <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 font-bold uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="px-3.5 py-3 text-left min-w-[240px]">Description of goods / services</th>
                      <th className="px-2.5 py-3 text-center w-28">HSN/SAC</th>
                      <th className="px-2.5 py-3 text-center w-24">Qty</th>
                      <th className="px-2.5 py-3 text-center w-32">Unit</th>
                      <th className="px-2.5 py-3 text-right w-32">Rate (₹)</th>
                      <th className="px-2.5 py-3 text-center w-24">Disc%</th>
                      <th className="px-2.5 py-3 text-right w-32">Taxable (₹)</th>
                      <th className="px-2.5 py-3 text-center w-40">Tax type</th>
                      {gstEnabled && <th className="px-2.5 py-3 text-center w-24">GST%</th>}
                      {gstEnabled && <th className="px-2.5 py-3 text-right w-32">Tax amt (₹)</th>}
                      <th className="px-2.5 py-3 text-right w-36">Total (₹)</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {lines.map(l => {
                      const baseRateViolation = violationsByLineKey.get(l.key);
                      return (
                      <Fragment key={l.key}>
                      <tr className={baseRateViolation
                        ? 'bg-amber-50/70 dark:bg-amber-950/20 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors'
                        : 'hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition-colors'}>
                        <td className="px-3 py-2.5 min-w-[240px] space-y-1.5">
                          <select
                            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 font-medium"
                            value={allMasterServices.find(s => s.name.toLowerCase() === (l.name || '').trim().toLowerCase())?.name || (l.name ? l.name : '')}
                            onChange={e => {
                              const val = e.target.value;
                              if (val) {
                                pickService(l.key, val);
                              } else {
                                setLine(l.key, { name: '', description: '' });
                              }
                            }}
                          >
                            <option value="">Select master service / Custom</option>
                            <optgroup label="Technology Services">
                              {masterServicesByCategory.TECHNOLOGY.map(s => (
                                <option key={s.name} value={s.name}>
                                  {s.name} (₹{(s.defaultRate || 0).toLocaleString('en-IN')})
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Marketing & Growth Services">
                              {masterServicesByCategory['MARKETING_GROWTH'].map(s => (
                                <option key={s.name} value={s.name}>
                                  {s.name} (₹{(s.defaultRate || 0).toLocaleString('en-IN')})
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Creative Services">
                              {masterServicesByCategory.CREATIVE.map(s => (
                                <option key={s.name} value={s.name}>
                                  {s.name} (₹{(s.defaultRate || 0).toLocaleString('en-IN')})
                                </option>
                              ))}
                            </optgroup>
                            {l.name && !allMasterServices.some(s => s.name.toLowerCase() === l.name.toLowerCase()) && (
                              <option value={l.name}>Custom: {l.name}</option>
                            )}
                          </select>
                          <input
                            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            value={l.name}
                            onChange={e => setLine(l.key, { name: e.target.value, description: e.target.value })}
                            placeholder="Or edit item name manually"
                          />
                          <textarea
                            rows={2}
                            className="w-full rounded-md border border-slate-200/80 dark:border-slate-700/80 bg-slate-50/80 dark:bg-slate-800/60 px-2.5 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500 text-slate-700 dark:text-slate-300 placeholder:text-slate-400 resize-y"
                            value={l.notes ?? ''}
                            onChange={e => setLine(l.key, { notes: e.target.value })}
                            placeholder="Add note / item specifications..."
                          />
                        </td>
                        <td className="px-2.5 py-2.5">
                          <input
                            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            value={l.hsnSac}
                            onChange={e => setLine(l.key, { hsnSac: e.target.value })}
                            placeholder="HSN"
                          />
                        </td>
                        <td className="px-2.5 py-2.5">
                          <input
                            type="number"
                            min={0.001}
                            step="any"
                            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            value={l.quantity}
                            onChange={e => setLine(l.key, { quantity: Number(e.target.value) })}
                          />
                        </td>
                        <td className="px-2.5 py-2.5 min-w-[90px]">
                          <select
                            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
                            value={UNIT_OPTIONS.find(u => u.toLowerCase() === (l.unit || 'nos').toLowerCase()) || l.unit || 'Nos'}
                            onChange={e => setLine(l.key, { unit: e.target.value })}
                          >
                            {UNIT_OPTIONS.map(u => (
                              <option key={u} value={u}>{u}</option>
                            ))}
                            {(l.unit || '') && !UNIT_OPTIONS.some(u => u.toLowerCase() === (l.unit || '').toLowerCase()) && (
                              <option value={l.unit || ''}>{l.unit || ''}</option>
                            )}
                          </select>
                        </td>
                        <td className="px-2.5 py-2.5">
                          <input
                            type="number"
                            min={0}
                            step="any"
                            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
                            value={l.rate}
                            onChange={e => setLine(l.key, { rate: Number(e.target.value) })}
                          />
                        </td>
                        <td className="px-2.5 py-2.5">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step="any"
                            className={"w-full rounded-md border bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-indigo-500 " + (baseRateViolation ? "border-amber-400 dark:border-amber-500" : "border-slate-200 dark:border-slate-700")}
                            value={l.discountPercent}
                            onChange={e => setLine(l.key, { discountPercent: Number(e.target.value) })}
                          />
                        </td>
                        <td className="px-2.5 py-2.5 text-right font-mono text-slate-700 dark:text-slate-300 whitespace-nowrap font-medium">
                          {fmtMoney(lineNet(l))}
                        </td>
                        <td className="px-2.5 py-2.5 min-w-[150px]">
                          <TaxSlabSelect
                            slabs={taxMasters}
                            value={l.taxMasterId ?? ''}
                            invalid={!l.taxMasterId}
                            placeholder="— Required —"
                            onChange={(taxMasterId, rate) => setLine(l.key, rate === undefined
                              ? { taxMasterId }
                              : { taxMasterId, taxRate: rate })}
                          />
                        </td>
                        {gstEnabled && <td className="px-2.5 py-2.5 min-w-[76px]">
                          <input
                            type="number"
                            min={0}
                            max={28}
                            className="w-full min-w-[68px] px-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            value={l.taxRate}
                            onChange={e => setLine(l.key, { taxRate: Number(e.target.value) })}
                          />
                        </td>}
                        {gstEnabled && <td className="px-2.5 py-2.5 text-right font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          {fmtMoney(lineTax(l, true))}
                        </td>}
                        <td className="px-2.5 py-2.5 text-right font-mono font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                          {fmtMoney(lineNet(l) + (gstEnabled ? lineTax(l, true) : 0))}
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          {lines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setLines(ls => ls.filter(x => x.key !== l.key))}
                              className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                              title="Remove line item"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                      {baseRateViolation && (
                        <tr className="bg-amber-50/80 dark:bg-amber-950/30">
                          <td
                            colSpan={9 + (gstEnabled ? 2 : 0)}
                            className="px-3.5 py-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                          >
                            ⚠ {baseRateWarningText(baseRateViolation)}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals Summary */}
              <div className="flex justify-end mt-4">
                <div className="w-72 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/90 dark:border-slate-700 space-y-2 text-xs">
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>Total Amount</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{fmtMoney(subtotal)}</span>
                  </div>
                  {gstEnabled && (
                    <>
                      {proformaInterState ? (
                        <div className="flex justify-between text-slate-600 dark:text-slate-400">
                          <span>IGST @ {lines[0]?.taxRate || 18}%</span>
                          <span className="font-medium text-slate-800 dark:text-slate-200 font-mono">{fmtMoney(taxTotal)}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between text-slate-600 dark:text-slate-400">
                            <span>CGST @ {(lines[0]?.taxRate || 18) / 2}%</span>
                            <span className="font-medium text-slate-800 dark:text-slate-200 font-mono">{fmtMoney(taxTotal / 2)}</span>
                          </div>
                          <div className="flex justify-between text-slate-600 dark:text-slate-400">
                            <span>SGST @ {(lines[0]?.taxRate || 18) / 2}%</span>
                            <span className="font-medium text-slate-800 dark:text-slate-200 font-mono">{fmtMoney(taxTotal / 2)}</span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <div className="flex justify-between border-t border-slate-200 dark:border-slate-700 pt-2 text-sm font-bold">
                    <span className="text-slate-900 dark:text-white">{gstEnabled ? 'Total payable' : 'Grand Total'}</span>
                    <span className="text-indigo-600 dark:text-indigo-400 font-mono text-base">{fmtMoney(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method & Milestones Section */}
            <div className={cardCls}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                    <CreditCard className="w-4 h-4" />
                  </div>
                <div>
                  <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                    Payment Method &amp; Schedule
                  </h3>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    {paymentType === 'milestone_based'
                      ? 'All stages are billed collectively under this single proforma'
                      : 'Single 100% full payment for the contract'}
                  </span>
                  {computedProformaNo && (
                    <span className="ml-2 inline-block text-[10px] text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 font-mono align-middle">
                      {computedProformaNo}
                    </span>
                  )}
                </div>
                </div>
                <span className="self-start sm:self-auto text-[11px] text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-full font-medium border border-slate-200 dark:border-slate-700">
                  Single Proforma Invoice
                </span>
              </div>

              {/* Payment Method Radio Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-4">
                <label
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    paymentType === 'full_payment'
                      ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/30 ring-1 ring-indigo-500 shadow-xs'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="proformaPaymentType"
                    value="full_payment"
                    checked={paymentType === 'full_payment'}
                    onChange={() => {
                      setPaymentType('full_payment');
                      setMilestones([]);
                    }}
                    className="mt-0.5 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Full Payment (100%)
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Single 100% payment for the entire contract amount ({fmtMoney(grandTotal)})
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    paymentType === 'milestone_based'
                      ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/30 ring-1 ring-indigo-500 shadow-xs'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="proformaPaymentType"
                    value="milestone_based"
                    checked={paymentType === 'milestone_based'}
                    onChange={() => {
                      setPaymentType('milestone_based');
                      if (milestones.length === 0) {
                        setMilestones([
                          { key: uid(), label: 'Stage 1: Advance / Mobilization', percent: 50, dueDate: '' },
                          { key: uid(), label: 'Stage 2: Final Delivery / Handover', percent: 50, dueDate: '' },
                        ]);
                      }
                    }}
                    className="mt-0.5 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Milestone-Based Payment
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Break down contract into percentage stages with GST splitting
                    </div>
                  </div>
                </label>
              </div>

              {/* Milestone Stages Table */}
              {paymentType === 'milestone_based' && (
                <div className="space-y-3.5 pt-2">
                  <div className="flex flex-wrap items-center justify-between gap-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-1">Presets:</span>
                      <button
                        type="button"
                        onClick={() => setMilestones([
                          { key: uid(), label: 'Stage 1: Advance', percent: 50, dueDate: '' },
                          { key: uid(), label: 'Stage 2: Final Delivery', percent: 50, dueDate: '' },
                        ])}
                        className="text-xs px-2.5 py-1 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        50% - 50%
                      </button>
                      <button
                        type="button"
                        onClick={() => setMilestones([
                          { key: uid(), label: 'Stage 1: Advance', percent: 40, dueDate: '' },
                          { key: uid(), label: 'Stage 2: Mid-term', percent: 30, dueDate: '' },
                          { key: uid(), label: 'Stage 3: Final Delivery', percent: 30, dueDate: '' },
                        ])}
                        className="text-xs px-2.5 py-1 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        40% - 30% - 30%
                      </button>
                      <button
                        type="button"
                        onClick={() => setMilestones([
                          { key: uid(), label: 'Stage 1: Advance', percent: 30, dueDate: '' },
                          { key: uid(), label: 'Stage 2: Phase 1', percent: 40, dueDate: '' },
                          { key: uid(), label: 'Stage 3: Completion', percent: 30, dueDate: '' },
                        ])}
                        className="text-xs px-2.5 py-1 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        30% - 40% - 30%
                      </button>
                      <button
                        type="button"
                        onClick={() => setMilestones([
                          { key: uid(), label: 'Stage 1: Advance', percent: 25, dueDate: '' },
                          { key: uid(), label: 'Stage 2: Milestone 1', percent: 25, dueDate: '' },
                          { key: uid(), label: 'Stage 3: Milestone 2', percent: 25, dueDate: '' },
                          { key: uid(), label: 'Stage 4: Final', percent: 25, dueDate: '' },
                        ])}
                        className="text-xs px-2.5 py-1 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        25% × 4
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        const remaining = Math.max(0, 100 - msPct);
                        setMilestones(prev => [
                          ...prev,
                          {
                            key: uid(),
                            label: `Stage ${prev.length + 1}`,
                            percent: remaining > 0 ? remaining : 10,
                            dueDate: '',
                          }
                        ]);
                      }}
                      className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-lg px-3 py-1.5 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Stage
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <table className="w-full text-xs min-w-[700px]">
                      <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 font-semibold">
                        <tr>
                          <th className="px-3 py-2.5 text-center w-10">#</th>
                          <th className="px-3 py-2.5 text-left">Stage / Milestone Name</th>
                          <th className="px-3 py-2.5 text-center w-28">% Share</th>
                          <th className="px-3 py-2.5 text-right w-28">Taxable (Net)</th>
                          {gstEnabled && <th className="px-3 py-2.5 text-right w-24">GST</th>}
                          <th className="px-3 py-2.5 text-right w-32">{gstEnabled ? 'Total (Incl. GST)' : 'Total'}</th>
                          <th className="px-3 py-2.5 text-left w-36">Due Date</th>
                          <th className="px-3 py-2.5 text-center w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {milestones.map((m, idx) => {
                          const pct = Number(m.percent) || 0;
                          const netAmt = Math.round((subtotal * (pct / 100)) * 100) / 100;
                          const taxAmt = Math.round((taxTotal * (pct / 100)) * 100) / 100;
                          const totalAmt = Math.round((grandTotal * (pct / 100)) * 100) / 100;

                          return (
                            <tr key={m.key} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                              <td className="px-3 py-2.5 text-center font-bold text-slate-400">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-2.5">
                                <input
                                  type="text"
                                  className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                                  value={m.label}
                                  onChange={e => setMs(m.key, { label: e.target.value })}
                                  placeholder={`Stage ${idx + 1} name`}
                                />
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="relative flex items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 pr-6 text-xs text-center font-medium focus:ring-1 focus:ring-indigo-500"
                                    value={m.percent === 0 ? '' : m.percent}
                                    onChange={e => setMs(m.key, { percent: e.target.value === '' ? 0 : Number(e.target.value) || 0 })}
                                    placeholder="0"
                                  />
                                  <span className="absolute right-2.5 text-slate-400 pointer-events-none text-xs">%</span>
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-right font-medium text-slate-700 dark:text-slate-300 font-mono">
                                {fmtMoney(netAmt)}
                              </td>
                              {gstEnabled && <td className="px-3 py-2.5 text-right text-slate-500 dark:text-slate-400 font-mono">
                                {fmtMoney(taxAmt)}
                              </td>}
                              <td className="px-3 py-2.5 text-right font-bold text-indigo-600 dark:text-indigo-400 font-mono">
                                {fmtMoney(totalAmt)}
                              </td>
                              <td className="px-3 py-2.5">
                                <input
                                  type="date"
                                  className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                                  value={m.dueDate || ''}
                                  onChange={e => setMs(m.key, { dueDate: e.target.value })}
                                />
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                {milestones.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setMilestones(prev => prev.filter(x => x.key !== m.key))}
                                    className="p-1.5 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                    title="Delete stage"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-50 dark:bg-slate-700/30 border-t border-slate-200 dark:border-slate-700 font-bold">
                        <tr>
                          <td colSpan={2} className="px-3.5 py-2.5 text-left text-slate-700 dark:text-slate-300">
                            Total Milestone Distribution
                          </td>
                          <td className="px-3.5 py-2.5 text-center">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                                msPct === 100
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                              }`}
                            >
                              {msPct}% {msPct === 100 ? '✓' : `(needs ${100 - msPct}%)`}
                            </span>
                          </td>
                          <td className="px-3.5 py-2.5 text-right text-slate-700 dark:text-slate-300 font-mono">
                            {fmtMoney(subtotal * (msPct / 100))}
                          </td>
                          <td className="px-3.5 py-2.5 text-right text-slate-500 dark:text-slate-400 font-mono">
                            {fmtMoney(taxTotal * (msPct / 100))}
                          </td>
                          <td className="px-3.5 py-2.5 text-right text-indigo-600 dark:text-indigo-400 font-mono">
                            {fmtMoney(grandTotal * (msPct / 100))}
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {msPct !== 100 && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                      <span className="font-bold">Note:</span>
                      <span>
                        Milestone stage percentages currently sum to <b>{msPct}%</b>. They must equal <b>100%</b> before submitting.
                      </span>
                    </div>
                  )}

                  <div className="text-[11px] text-slate-500 dark:text-slate-400 italic">
                    ℹ All stages above are recorded collectively on this single Proforma Invoice. No separate invoices will be generated for individual stages.
                  </div>
                </div>
              )}
            </div>

            {/* Bank & Settlement Details Card */}
            <div className={cardCls}>
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                  <Landmark className="w-4 h-4" />
                </div>
                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                  Bank &amp; Settlement Details
                </h3>
              </div>

              <div className="-mt-2 mb-3"><LockedHint source="bank" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className={labelCls}>Select Bank from Master Data</label>
                  <select className={cls} value={bankDetailId} onChange={e => {
                    const selected = bankDetails.find(b => b.id === e.target.value);
                    if (selected) {
                      setBankDetailId(selected.id);
                      setBankAccName(selected.accountName || '');
                      setBankAccNo(selected.accountNumber || '');
                      setBankName(selected.bankName || '');
                      setBankBranch(selected.branch || '');
                      setBankIfsc(selected.ifsc || '');
                      setBankUpi(selected.upi || '');
                    } else {
                      setBankDetailId('');
                    }
                  }}>
                    <option value="">{bankAccNo ? "— As saved on this document —" : "— Select a bank account —"}</option>
                    {bankDetails.filter(b => b.isActive).map(b => (
                      <option key={b.id} value={b.id}>{b.bankName} {b.accountNumber ? `• ${b.accountNumber}` : ''} {b.accountName ? `(${b.accountName})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Account Name</label>
                  <input className={cls + " " + LOCKED_INPUT_CLASS} value={bankAccName} readOnly aria-readonly="true" />
                </div>
                <div>
                  <label className={labelCls}>Account Number</label>
                  <input className={cls + " " + LOCKED_INPUT_CLASS} value={bankAccNo} readOnly aria-readonly="true" />
                </div>
                <div>
                  <label className={labelCls}>Bank Name</label>
                  <input className={cls + " " + LOCKED_INPUT_CLASS} value={bankName} readOnly aria-readonly="true" />
                </div>
                <div>
                  <label className={labelCls}>Branch</label>
                  <input className={cls + " " + LOCKED_INPUT_CLASS} value={bankBranch} readOnly aria-readonly="true" />
                </div>
                <div>
                  <label className={labelCls}>IFSC Code</label>
                  <input className={cls + " " + LOCKED_INPUT_CLASS} value={bankIfsc} readOnly aria-readonly="true" />
                </div>
                <div>
                  <label className={labelCls}>UPI ID</label>
                  <input className={cls + " " + LOCKED_INPUT_CLASS} value={bankUpi} readOnly aria-readonly="true" />
                </div>
              </div>
            </div>

            {/* Terms, Conditions & Notes Card */}
            <div className={cardCls}>
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                  <FileCheck className="w-4 h-4" />
                </div>
                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                  Notes, Terms &amp; Conditions
                </h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Special Notes / Instructions</label>
                  <textarea
                    rows={4}
                    className={cls}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Payment instructions, bank transfer notes, delivery conditions..."
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={labelCls + " !mb-0"}>Terms &amp; Conditions</label>
                    {defaultTermsArray.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setTermsList([...defaultTermsArray]);
                          setTerms(defaultTerms);
                          toast.success(`Loaded ${defaultTermsArray.length} default terms`);
                        }}
                        className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Reset to Defaults
                      </button>
                    )}
                  </div>

                  <select
                    className={cls + " mb-2.5"}
                    value=""
                    onChange={e => {
                      const val = e.target.value;
                      if (!val) return;
                      if (val === 'custom') {
                        setTermsList(prev => [...prev, '']);
                      } else {
                        const selected = termConditions.find(t => t.id === val);
                        if (selected) {
                          const content = (selected.content || '').trim();
                          const termsArr = content ? content.split('\n').map((s: string) => s.trim()).filter(Boolean) : [selected.title];
                          setTermsList(prev => {
                            const cur = prev.filter(s => s && s.trim());
                            const updated = [...cur, ...termsArr];
                            setTerms(updated.join('\n'));
                            return updated;
                          });
                          toast.success(`Added "${selected.title}"`);
                        }
                      }
                    }}
                  >
                    <option value="">— Add term from master data —</option>
                    {(termConditions || []).filter(t => t.isActive !== false).map(t => (
                      <option key={t.id} value={t.id}>{t.title}</option>
                    ))}
                    <option value="custom">+ Custom empty term</option>
                  </select>

                  <div className="space-y-2">
                    {termsList.map((term, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-400 w-5 text-right">{idx + 1}.</span>
                        <input
                          className={cls}
                          value={term}
                          onChange={e => {
                            const n = [...termsList];
                            n[idx] = e.target.value;
                            setTermsList(n);
                            setTerms(n.filter(s => s.trim()).join('\n'));
                          }}
                          placeholder="Term text..."
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const n = termsList.filter((_, i) => i !== idx);
                            setTermsList(n);
                            setTerms(n.filter(s => s.trim()).join('\n'));
                          }}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setTermsList([...termsList, ''])}
                    className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:hover:text-indigo-400 transition-colors mt-2.5"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add term
                  </button>
                </div>
              </div>
            </div>

            {/* Assumptions, Exclusions & Declaration Card */}
            <div className={cardCls}>
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                  <HelpCircle className="w-4 h-4" />
                </div>
                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                  {assumptionsSectionEnabled ? (
                    <>Assumptions, Exclusions &amp; Declaration</>
                  ) : (
                    <>Declaration</>
                  )}
                </h3>
              </div>

              <div className="space-y-5">
                {/* Assumptions & Exclusions (hidden when disabled in master data) */}
                {assumptionsSectionEnabled && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={labelCls + " !mb-0"}>Assumptions &amp; Exclusions</label>
                    {defaultAssumptionsArray.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setAssumptionsList([...defaultAssumptionsArray]);
                          setAssumptions(defaultAssumptions + (defaultExclusions ? '\n' + defaultExclusions : ''));
                          toast.success(`Loaded ${defaultAssumptionsArray.length} default assumptions & exclusions`);
                        }}
                        className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Reset to Defaults
                      </button>
                    )}
                  </div>

                  <select
                    className={cls + " mb-2.5"}
                    value=""
                    onChange={e => {
                      const val = e.target.value;
                      if (!val) return;
                      if (val === 'custom') {
                        setAssumptionsList(prev => [...prev, '']);
                      } else {
                        const selected = assumptionsExclusions.find(a => a.id === val);
                        if (selected) {
                          const isExcl = String(selected.category || '').toUpperCase() === 'EXCLUSION';
                          const prefix = isExcl ? 'EXCLUSION: ' : '';
                          const itemText = prefix + selected.description;
                          setAssumptionsList(prev => {
                            const cur = prev.filter(s => s && s.trim());
                            const updated = [...cur, itemText];
                            setAssumptions(updated.join('\n'));
                            return updated;
                          });
                          toast.success(`Added "${selected.title}"`);
                        }
                      }
                    }}
                  >
                    <option value="">— Add assumption / exclusion from master data —</option>
                    {(assumptionsExclusions || []).filter(a => a.isActive !== false).map(a => {
                      const isExcl = String(a.category || '').toUpperCase() === 'EXCLUSION';
                      return (
                        <option key={a.id} value={a.id}>
                          {(isExcl ? 'EXCLUSION: ' : 'ASSUMPTION: ')}{a.title ? `${a.title} — ` : ''}{a.description.slice(0, 55)}
                        </option>
                      );
                    })}
                    <option value="custom">+ Custom empty item</option>
                  </select>

                  <div className="space-y-2">
                    {assumptionsList.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-400 w-5 text-right">{idx + 1}.</span>
                        <input
                          className={cls}
                          value={item}
                          onChange={e => {
                            const n = [...assumptionsList];
                            n[idx] = e.target.value;
                            setAssumptionsList(n);
                            setAssumptions(n.filter(s => s.trim()).join('\n'));
                          }}
                          placeholder="Assumption or exclusion item..."
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const n = assumptionsList.filter((_, i) => i !== idx);
                            setAssumptionsList(n);
                            setAssumptions(n.filter(s => s.trim()).join('\n'));
                          }}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setAssumptionsList([...assumptionsList, ''])}
                    className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:hover:text-indigo-400 transition-colors mt-2.5"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add assumption/exclusion
                  </button>
                </div>
                )}

                {/* Declaration */}
                <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                  <label className={labelCls}>Declaration</label>
                  <select
                    className={cls + " mb-2.5"}
                    value={declarationId}
                    onChange={e => {
                      const selected = declarations.find(d => d.id === e.target.value);
                      if (selected) { setDeclarationId(selected.id); setDeclaration(selected.content); }
                      else { setDeclarationId(''); setDeclaration(''); }
                    }}
                  >
                    <option value="">— Custom declaration —</option>
                    {(declarations || []).filter(d => d.isActive).map(d => (
                      <option key={d.id} value={d.id}>{d.title || 'Declaration'}</option>
                    ))}
                  </select>
                  <textarea
                    rows={2}
                    className={cls}
                    value={declaration}
                    onChange={e => setDeclaration(e.target.value)}
                    placeholder="Declaration statement displayed before signatory..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons Footer */}
          {baseRateViolations.length > 0 && (
            <BaseRateWarningBanner violations={baseRateViolations} documentLabel="proforma" />
          )}
          <div className="flex flex-wrap items-center justify-end gap-3 pt-5 mt-5 border-t border-slate-200 dark:border-slate-800">
            {isConfirmed ? (
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold border border-slate-300 dark:border-slate-700 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 flex items-center gap-2 transition-colors"
              >
                <Lock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                Close (Locked)
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-lg text-sm border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
                >
                  Cancel
                </button>
                {!isResubmitMode && !isRevision && (
                  <button
                    type="button"
                    disabled={saving || masterDataBlocked}
                    onClick={() => handleSave("draft")}
                    className="px-5 py-2.5 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2 transition-colors"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save as Draft
                  </button>
                )}
                {!approvesOwnProforma ? (
                  // A proforma is not locked until the superadmin approves it, so the rep submits it for approval.
                  ['proforma_requested', 'proforma_under_review'].includes(String((editingDraft as any)?.status || '')) ? (
                    <button
                      type="button"
                      disabled={saving || masterDataBlocked}
                      onClick={() => handleSave("draft")}
                      className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 shadow-xs transition-colors"
                    >
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                      Save Changes
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={saving || masterDataBlocked || baseRateViolations.length > 0}
                      onClick={() => handleSave("submit")}
                      className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 shadow-xs transition-colors"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                      Submit for Approval
                    </button>
                  )
                ) : (
                  // The superadmin's own proforma is approved as it is submitted, and is then final.
                  <button
                    type="button"
                    disabled={saving || masterDataBlocked || baseRateViolations.length > 0}
                    onClick={() => handleSave("submit")}
                    className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 shadow-xs transition-colors"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                    Submit &amp; Approve
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
